// The object layer: users see OBJECTS (points/lines/polygons), not layers.
// Display path is MVT from /api/tiles/entities (authz-filtered server-side,
// asof-ready for the future time slider); a client GeoJSON source carries
// selected/just-edited entities for instant feedback without tile churn.
//
// Marker design, glance-value first:
//  - units: compact flat chip in the type colour with a simple white glyph,
//    plus a thin status ring (green ready / amber limited / grey offline)
//  - damage markers: small ✕, no chip — they are background texture
//  - live events: client-side age-coloured dots (red→orange→amber→grey) with
//    a soft halo while fresh, so "what just happened" pops off the map
//  - coverage: engagement ring (solid + faint fill) and radar ring (dashed)

import type { Feature, Point } from 'geojson';
import * as maplibregl from 'maplibre-gl';
import { updateAlerts } from './alerts';
import type { EntityDto, RegistryDto } from './api';
import { get } from './api';
import type { EventMarkerStyle } from './eventstyle';
import {
  DEFAULT_MARKER_STYLE, newMs, nextBoundaryMs, pulseFactor, pulseMs, ringMs,
  ringProgress, stageAt, styleFor,
} from './eventstyle';
import { registerHover } from './hover';
import { knownKind, LIVE_KINDS } from './livekinds';
import { normalizeBearing, type RadarPlatform, type RadarPose, type RadarSpec } from './radar';
import { groupFeed } from './incidents';
import { headlineOf, situationAnchor } from './situations';
import { spread } from './spread';
import { activeSubtreeIds, isOnlyThisStory } from './story';
import { AGE_COLORS, ageColor, ageOpacity, lastReportOf, liveFreshMs, visibilityOf, whenOf } from './when';

const SRC = 'entities';
const SEL = 'objects-selected';
const COVERAGE = 'ad-coverage';
const LIVE = 'live-events';
const SPLASH = 'live-splash-src';
/** Legs from a fanned-out marker back to the point it stands for (spread.ts). */
const LEGS = 'live-legs-src';

/** Every layer drawn from the client-side live-event source, bottom-first —
 *  ONE definition, because four call sites (the Events toggle, the replay view,
 *  the click targets, and daynight's insert-below probe) each used to carry
 *  their own copy and a renamed layer silently dropped out of whichever list
 *  was missed. `live-splash` is replay-only and stays out of the click list. */
export const LIVE_LAYER_IDS = ['live-splash', 'live-leg', 'live-halo', 'live-ring',
                               'live-dot', 'live-glyph', 'live-glyph-new'];
/** The subset a click may select an event through — a ring is 26 px of mostly
 *  empty space and would hijack clicks meant for whatever is under it. */
export const LIVE_CLICK_IDS = ['live-dot', 'live-glyph', 'live-glyph-new'];

const STATUS_COLORS: Record<string, string> = {
  ready: '#2e9e6b', limited: '#e0a63d', offline: '#8a97a0',
};

// Live-event timing and the age ramps now live in ./when — see that module for
// why. It is the one place that decides which clock answers which question, and
// its helpers take epoch MILLISECONDS so a wrong-clock call site cannot compile.

let registry: RegistryDto | null = null;
let tileVersion = 0;
let tileBust = 0;

export function getRegistry(): RegistryDto | null {
  return registry;
}

export async function loadRegistry(): Promise<RegistryDto> {
  registry = await get<RegistryDto>('/api/registry');
  return registry;
}

function colorMatch(fallback: string): unknown {
  const expr: unknown[] = ['match', ['get', 'type_key']];
  for (const t of registry?.types ?? []) {
    expr.push(t.key, t.style.color ?? fallback);
  }
  expr.push(fallback);
  return expr;
}

// --- territorial control (migration 054) -------------------------------------
// Territories are polygon entities in the `control` category, drawn by their
// OWN layers below every other entity polygon: coloured by the holder (the
// tile's `faction` property, matched against the colours territory.ts
// derives from the faction entities), hatched and dash-outlined when
// contested (`status`, a tile property since 022). They are excluded from the
// generic polygon layers so a country-sized fill never sits on top of a base
// outline, and so the generic colour-by-type never paints ground.
export const CONTROL_LAYER_IDS = [
  'control-fill', 'control-hatch', 'control-outline', 'control-outline-contested',
];
const IS_CONTROL: unknown = ['==', ['get', 'category_key'], 'control'];
const NOT_CONTROL: unknown = ['!=', ['get', 'category_key'], 'control'];
const IS_CONTESTED: unknown = ['==', ['coalesce', ['get', 'status'], ''], 'contested'];
/** Each control layer's standing filter — what it draws before any panel
 *  narrowing. applyObjectFilter ANDs the shared narrowing onto these, and the
 *  replay view restores THESE rather than null (a null filter on the fill
 *  would paint every polygon in the holder colour). */
export const CONTROL_BASE_FILTERS: Record<string, unknown> = {
  'control-fill': IS_CONTROL,
  'control-hatch': ['all', IS_CONTROL, IS_CONTESTED],
  'control-outline': ['all', IS_CONTROL, ['!', IS_CONTESTED]],
  'control-outline-contested': ['all', IS_CONTROL, IS_CONTESTED],
};
const CONTROL_FALLBACK = '#8a6d3b';
let factionColors: Record<string, string> = {};

/** How CONTESTED ground is hatched and dash-outlined (the `contested` half of
 *  the territoryColors setting, 062): in the HOLDER's colour — the convention
 *  that shipped —, in the CONTESTER's (the tile's `contested_by`, so a front
 *  reads as "whose ground, pressed by whom"), or in one FIXED colour that
 *  says "disputed" the same way everywhere. */
export interface ContestedStyle { hatch: 'holder' | 'contester' | 'fixed'; color: string }
export const DEFAULT_CONTESTED_STYLE: ContestedStyle = { hatch: 'holder', color: '#d62728' };
/** The pattern image every fixed-colour hatch shares. */
export const FIXED_HATCH_IMAGE = 'hatch-contested';
let contestedStyle: ContestedStyle = { ...DEFAULT_CONTESTED_STYLE };

/** The holder's colour, by the tile's `faction`. */
function colorMatchExpr(prop: string, colors: Record<string, string>,
                        fallback: unknown): unknown {
  const pairs = Object.entries(colors);
  // `match` needs at least one branch; with no factions known, the fallback.
  if (!pairs.length) return fallback;
  const expr: unknown[] = ['match', ['coalesce', ['get', prop], '']];
  for (const [name, color] of pairs) expr.push(name, color);
  expr.push(fallback);
  return expr;
}

function controlColorExpr(colors = factionColors): unknown {
  return colorMatchExpr('faction', colors, CONTROL_FALLBACK);
}

/** The hatch pattern for contested ground, per mode. A contester with no
 *  pattern of its own (the name arrived before the colours did) falls back
 *  to the holder's, then to `styleimagemissing`'s fallback-coloured one. */
function contestedPatternExpr(contested = contestedStyle): unknown {
  if (contested.hatch === 'fixed') return FIXED_HATCH_IMAGE;
  const name = contested.hatch === 'contester'
    ? ['coalesce', ['get', 'contested_by'], ['get', 'faction'], '']
    : ['coalesce', ['get', 'faction'], ''];
  return ['image', ['concat', 'hatch-', name]];
}

/** The dashed outline's colour for contested ground: the holder's, the
 *  contester's (falling back to the holder's), or the fixed colour. */
function contestedLineColorExpr(colors = factionColors, contested = contestedStyle): unknown {
  if (contested.hatch === 'fixed') return contested.color;
  const holder = controlColorExpr(colors);
  if (contested.hatch === 'contester') return colorMatchExpr('contested_by', colors, holder);
  return holder;
}

/** The control layers, bottom-first — a pure function so objects.test.ts can
 *  run the style spec over them (a malformed expression here fails the whole
 *  map at construction, with nothing partial to notice). The optional
 *  arguments let the test build every contested mode and a populated colour
 *  map; the map itself is built from the module state. */
export function controlLayers(opts: { colors?: Record<string, string>;
                                      contested?: ContestedStyle } = {}): unknown[] {
  const colors = opts.colors ?? factionColors;
  const contested = opts.contested ?? contestedStyle;
  const color = controlColorExpr(colors);
  return [
    {
      id: 'control-fill', type: 'fill', source: SRC, 'source-layer': 'polygons',
      filter: CONTROL_BASE_FILTERS['control-fill'],
      paint: {
        'fill-color': color,
        // Strong enough to read as a coloured country on a grey basemap;
        // contested ground is lighter because the hatch layer sits on it.
        'fill-opacity': ['case', IS_CONTESTED, 0.16, 0.3],
      },
    },
    {
      // Diagonal hatching in the holder's colour marks CONTESTED ground — the
      // convention every control map uses. One pattern image per faction,
      // named by the tile's own `faction` string (setFactionColors).
      id: 'control-hatch', type: 'fill', source: SRC, 'source-layer': 'polygons',
      filter: CONTROL_BASE_FILTERS['control-hatch'],
      paint: {
        'fill-pattern': contestedPatternExpr(contested),
        'fill-opacity': 0.55,
      },
    },
    {
      id: 'control-outline', type: 'line', source: SRC, 'source-layer': 'polygons',
      filter: CONTROL_BASE_FILTERS['control-outline'],
      paint: { 'line-color': color, 'line-width': 1.8, 'line-opacity': 0.9 },
    },
    {
      // line-dasharray cannot be data-driven, hence a second outline layer.
      id: 'control-outline-contested', type: 'line', source: SRC, 'source-layer': 'polygons',
      filter: CONTROL_BASE_FILTERS['control-outline-contested'],
      paint: {
        'line-color': contestedLineColorExpr(colors, contested),
        'line-width': 1.8, 'line-opacity': 0.9,
        'line-dasharray': [3, 2],
      },
    },
  ];
}

function drawHatch(color: string): ImageData {
  const size = 16;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d')!;
  ctx.strokeStyle = color;
  ctx.lineWidth = 2.2;
  ctx.lineCap = 'butt';
  // Three 45° strokes so the tile repeats seamlessly.
  for (const [x0, y0, x1, y1] of [[-4, 12, 12, -4], [0, 16, 16, 0], [4, 20, 20, 4]]) {
    ctx.beginPath();
    ctx.moveTo(x0!, y0!);
    ctx.lineTo(x1!, y1!);
    ctx.stroke();
  }
  return ctx.getImageData(0, 0, size, size);
}

/** What the map currently carries, so a repeat with the same colours is a
 *  no-op. This is called on EVERY poll (rebuildViews), and unconditionally
 *  re-adding a pattern image makes MapLibre reload every tile that uses it,
 *  while re-setting a data-driven paint property re-evaluates it over every
 *  feature of the layer — both for colours that had not changed. */
let appliedFactionColors: Record<string, string> | null = null;
let appliedContested: ContestedStyle | null = null;

/** Give every holder (and contester) its colour: the fill/outline expressions
 *  and one hatch pattern per faction, plus the contested look. Called by
 *  territory.ts whenever the entity list or the colour setting changes; a
 *  faction whose colour changed gets a fresh pattern. */
export function setFactionColors(map: maplibregl.Map, colors: Record<string, string>,
                                 contested: ContestedStyle = contestedStyle): void {
  const names = Object.keys(colors);
  const prev = appliedFactionColors;
  const sameContested = appliedContested?.hatch === contested.hatch
    && appliedContested.color === contested.color
    && (contested.hatch !== 'fixed' || map.hasImage(FIXED_HATCH_IMAGE));
  if (prev && sameContested && names.length === Object.keys(prev).length
      && names.every((n) => prev[n] === colors[n] && map.hasImage(`hatch-${n}`))
      && map.getLayer('control-fill')) {
    return;
  }
  factionColors = { ...colors };
  contestedStyle = { ...contested };
  for (const [name, color] of Object.entries(colors)) {
    const id = `hatch-${name}`;
    if (prev?.[name] === color && map.hasImage(id)) continue;   // pattern unchanged
    if (map.hasImage(id)) map.removeImage(id);
    map.addImage(id, drawHatch(color), { pixelRatio: 2 });
  }
  if (contested.hatch === 'fixed'
      && !(appliedContested?.color === contested.color && map.hasImage(FIXED_HATCH_IMAGE))) {
    // styleimagemissing may have minted a fallback-coloured one already.
    if (map.hasImage(FIXED_HATCH_IMAGE)) map.removeImage(FIXED_HATCH_IMAGE);
    map.addImage(FIXED_HATCH_IMAGE, drawHatch(contested.color), { pixelRatio: 2 });
  }
  const color = controlColorExpr();
  if (map.getLayer('control-fill')) map.setPaintProperty('control-fill', 'fill-color', color as never);
  if (map.getLayer('control-outline')) map.setPaintProperty('control-outline', 'line-color', color as never);
  if (map.getLayer('control-hatch')) {
    map.setPaintProperty('control-hatch', 'fill-pattern', contestedPatternExpr() as never);
  }
  if (map.getLayer('control-outline-contested')) {
    map.setPaintProperty('control-outline-contested', 'line-color',
                         contestedLineColorExpr() as never);
  }
  appliedFactionColors = { ...colors };
  appliedContested = { ...contested };
}

// --- marker images -----------------------------------------------------------
// 56 px canvas at pixelRatio 2 → 28 px logical chip. Flat colour, white glyph,
// hairline dark rim so it reads on bright basemaps.

type Ctx = CanvasRenderingContext2D;

function glyph(ctx: Ctx, icon: string): void {
  ctx.strokeStyle = 'rgba(255,255,255,.96)';
  ctx.fillStyle = 'rgba(255,255,255,.96)';
  ctx.lineWidth = 3.2;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  const line = (pts: [number, number][]) => {
    ctx.beginPath();
    ctx.moveTo(pts[0]![0], pts[0]![1]);
    for (const [px, py] of pts.slice(1)) ctx.lineTo(px, py);
    ctx.stroke();
  };
  switch (icon) {
    case 'missile':
      line([[22, 25], [28, 16], [34, 25]]);
      line([[28, 17], [28, 37]]);
      line([[22, 41], [28, 34], [34, 41]]);
      break;
    case 'gun':
      ctx.beginPath(); ctx.arc(24, 34, 6, 0, Math.PI * 2); ctx.stroke();
      line([[29, 29], [40, 18]]);
      break;
    case 'radar':
      ctx.beginPath(); ctx.arc(23, 34, 2.2, 0, Math.PI * 2); ctx.fill();
      ctx.beginPath(); ctx.arc(23, 34, 8, -Math.PI / 2.6, Math.PI / 9); ctx.stroke();
      ctx.beginPath(); ctx.arc(23, 34, 14, -Math.PI / 2.6, Math.PI / 9); ctx.stroke();
      break;
    case 'artillery':
      ctx.beginPath(); ctx.arc(23, 37, 5, 0, Math.PI * 2); ctx.stroke();
      line([[26, 32], [39, 17]]);
      line([[34, 15], [41, 21]]);
      break;
    case 'tank':
      ctx.beginPath();
      if (typeof ctx.roundRect === 'function') ctx.roundRect(17, 30, 21, 9, 4.5);
      else ctx.rect(17, 30, 21, 9);
      ctx.stroke();
      line([[28, 30], [41, 22]]);
      break;
    case 'apc':
      ctx.beginPath(); ctx.rect(18, 27, 20, 10); ctx.stroke();
      ctx.beginPath(); ctx.arc(23, 40, 1.6, 0, Math.PI * 2); ctx.fill();
      ctx.beginPath(); ctx.arc(33, 40, 1.6, 0, Math.PI * 2); ctx.fill();
      break;
    case 'truck':
      ctx.beginPath(); ctx.rect(17, 26, 11, 11); ctx.stroke();
      ctx.beginPath(); ctx.rect(28, 30, 11, 7); ctx.stroke();
      break;
    case 'ship':
      line([[16, 33], [40, 33], [35, 40], [21, 40], [16, 33]]);
      line([[28, 33], [28, 22]]);
      line([[22, 27], [34, 27]]);
      break;
    case 'plane':
      line([[28, 16], [28, 40]]);
      line([[16, 28], [40, 28]]);
      line([[23, 38], [33, 38]]);
      break;
    case 'helo':
      ctx.beginPath(); ctx.arc(26, 32, 5.5, 0, Math.PI * 2); ctx.stroke();
      line([[15, 20], [39, 20]]);
      line([[26, 26], [26, 20]]);
      line([[31, 33], [41, 31]]);
      break;
    case 'uav':
      line([[15, 33], [28, 24], [41, 33]]);
      ctx.beginPath(); ctx.arc(28, 29, 2.2, 0, Math.PI * 2); ctx.fill();
      break;
    case 'bookmark':
      line([[22, 42], [22, 16]]);
      line([[22, 18], [37, 22], [22, 27]]);
      break;
    case 'shield':
      line([[28, 15], [39, 19], [37, 32]]);
      ctx.quadraticCurveTo(35, 40, 28, 44);
      ctx.quadraticCurveTo(21, 40, 19, 32);
      ctx.lineTo(17, 19); ctx.closePath(); ctx.stroke();
      break;
    case 'burst':
    case 'pulse':
      line([[20, 20], [36, 36]]);
      line([[36, 20], [20, 36]]);
      break;
    case 'nuclear': {
      // radiation trefoil: centre dot + three fan blades
      ctx.beginPath(); ctx.arc(28, 28, 2.4, 0, Math.PI * 2); ctx.fill();
      for (const a of [-Math.PI / 2, Math.PI / 6, (5 * Math.PI) / 6]) {
        ctx.beginPath();
        ctx.moveTo(28, 28);
        ctx.arc(28, 28, 12, a - 0.52, a + 0.52);
        ctx.closePath();
        ctx.fill();
      }
      break;
    }
    case 'power':
      // lightning bolt
      ctx.beginPath();
      ctx.moveTo(31, 15);
      ctx.lineTo(21, 31); ctx.lineTo(28, 31);
      ctx.lineTo(25, 41); ctx.lineTo(37, 25); ctx.lineTo(30, 25);
      ctx.closePath();
      ctx.fill();
      break;
    case 'refinery':
      // three distillation columns on a base, the tallest flaring
      line([[16, 40], [40, 40]]);
      line([[21, 40], [21, 24]]);
      line([[28, 40], [28, 18]]);
      line([[35, 40], [35, 28]]);
      ctx.beginPath(); ctx.arc(28, 14.5, 2.2, 0, Math.PI * 2); ctx.fill();
      break;
    case 'oilgas':
      // a drop
      ctx.beginPath();
      ctx.moveTo(28, 15);
      ctx.bezierCurveTo(22, 24, 19, 29, 19, 33);
      ctx.arc(28, 33, 9, Math.PI, 0, true);
      ctx.bezierCurveTo(37, 29, 34, 24, 28, 15);
      ctx.closePath();
      ctx.fill();
      break;
    default:
      ctx.beginPath(); ctx.arc(28, 28, 3.6, 0, Math.PI * 2); ctx.fill();
  }
}

export function drawChip(color: string, icon: string): ImageData {
  const canvas = document.createElement('canvas');
  canvas.width = 56;
  canvas.height = 56;
  const ctx = canvas.getContext('2d')!;
  ctx.clearRect(0, 0, 56, 56);
  // hairline rim → colour disc → glyph
  ctx.fillStyle = 'rgba(10, 20, 24, .28)';
  ctx.beginPath(); ctx.arc(28, 28, 24, 0, Math.PI * 2); ctx.fill();
  ctx.fillStyle = color;
  ctx.beginPath(); ctx.arc(28, 28, 22.5, 0, Math.PI * 2); ctx.fill();
  glyph(ctx, icon);
  return ctx.getImageData(0, 0, 56, 56);
}

// Live-event kind glyphs: the age ramp owns the dot's COLOR, so kind gets the
// SHAPE — the feed's emoji vocabulary rendered to sprites, floated just above
// each dot. A drop shadow keeps them legible over bright basemaps.
const GLYPH_BOX = 44;      // device px at pixelRatio 2 → 22 logical px
const GLYPH_FONT = 30;
const GLYPH_FACE =
  '"Segoe UI Emoji", "Apple Color Emoji", "Noto Color Emoji", sans-serif';

/** Draw one glyph, centred, at `scale` of its nominal size. `tint` renders it
 *  as a solid silhouette in that colour instead of the natural colour emoji —
 *  a colour-emoji font cannot be recoloured any other way, and the silhouette
 *  is what lets a style say "the symbol is red at first, then grey". */
function paintGlyph(ctx: Ctx, glyphChar: string, box: number,
                    scale: number, tint: string): void {
  ctx.clearRect(0, 0, box, box);
  ctx.save();
  ctx.font = `${GLYPH_FONT * scale}px ${GLYPH_FACE}`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  if (!tint) {
    ctx.shadowColor = 'rgba(0, 0, 0, .45)';
    ctx.shadowBlur = 3;
    ctx.shadowOffsetY = 1;
    ctx.fillText(glyphChar, box / 2, box / 2 + 1);
    ctx.restore();
    return;
  }
  // Silhouette: the emoji's own alpha filled flat, then re-drawn through a
  // shadow so it still reads on a bright basemap. `source-in` would eat the
  // shadow if it were applied in one pass, hence the scratch canvas.
  const scratch = document.createElement('canvas');
  scratch.width = box;
  scratch.height = box;
  const sctx = scratch.getContext('2d')!;
  sctx.font = ctx.font;
  sctx.textAlign = 'center';
  sctx.textBaseline = 'middle';
  sctx.fillText(glyphChar, box / 2, box / 2 + 1);
  sctx.globalCompositeOperation = 'source-in';
  sctx.fillStyle = tint;
  sctx.fillRect(0, 0, box, box);
  ctx.shadowColor = 'rgba(0, 0, 0, .5)';
  ctx.shadowBlur = 3;
  ctx.shadowOffsetY = 1;
  ctx.drawImage(scratch, 0, 0);
  ctx.restore();
}

function drawLiveGlyph(glyphChar: string, tint = ''): ImageData {
  const canvas = document.createElement('canvas');
  canvas.width = GLYPH_BOX;
  canvas.height = GLYPH_BOX;
  const ctx = canvas.getContext('2d')!;
  paintGlyph(ctx, glyphChar, GLYPH_BOX, 1, tint);
  return ctx.getImageData(0, 0, GLYPH_BOX, GLYPH_BOX);
}

// Damage markers are frequent background texture: a bare ✕ with an outline,
// no chip, so they never compete with units for attention.
function drawDamage(color: string): ImageData {
  const canvas = document.createElement('canvas');
  canvas.width = 36;
  canvas.height = 36;
  const ctx = canvas.getContext('2d')!;
  ctx.lineCap = 'round';
  ctx.strokeStyle = 'rgba(255,255,255,.9)';
  ctx.lineWidth = 7;
  ctx.beginPath();
  ctx.moveTo(11, 11); ctx.lineTo(25, 25);
  ctx.moveTo(25, 11); ctx.lineTo(11, 25);
  ctx.stroke();
  ctx.strokeStyle = color;
  ctx.lineWidth = 4;
  ctx.beginPath();
  ctx.moveTo(11, 11); ctx.lineTo(25, 25);
  ctx.moveTo(25, 11); ctx.lineTo(11, 25);
  ctx.stroke();
  return ctx.getImageData(0, 0, 36, 36);
}

function installMarkerImages(map: maplibregl.Map): void {
  for (const t of registry?.types ?? []) {
    const name = `entity-${t.key}`;
    if (map.hasImage(name)) continue;
    const color = t.style.color ?? '#4f7fd9';
    map.addImage(name, t.key === 'damage_marker'
      ? drawDamage(color)
      : drawChip(color, t.style.icon ?? 'dot'), { pixelRatio: 2 });
  }
}

/** After the registry gains a type at runtime, give its markers an image. */
export function ensureTypeImages(map: maplibregl.Map): void {
  installMarkerImages(map);
}

// --- style-driven event marker sprites ---------------------------------------
// Everything that MOVES on a live-event marker is a self-animating sprite, not
// a per-frame paint update. MapLibre calls render() once a frame while an image
// is on screen and nothing at all when it is off, so an animation costs exactly
// what is visible — where re-setting a data-driven paint property every frame
// re-evaluates it over every feature in the source and re-uploads the buffer,
// and re-setting a LAYOUT property (icon-size) re-lays out the whole symbol
// layer. That is the reason the size pulse lives inside the glyph sprite rather
// than in an animated `icon-size`: it is the only form of it that stays cheap.
//
// The cost of that choice is that every marker sharing a sprite pulses IN
// PHASE. That is a real difference from a per-feature animation and it reads
// fine — arguably better, since a burst of arrivals beats together — but it is
// a property of the design, not an accident.
//
// Sprites are built lazily and cached by a name derived from the parameters
// that shape them, so two kinds configured identically share one texture and a
// config change simply starts naming different sprites.

const spriteNames = new Set<string>();
// A ring sprite can be up to ~400 device px square. The name includes the
// resolved colour, so a style set to follow the age ramp mints one per stop;
// this ceiling is the backstop against a pathological config (many kinds ×
// many stages) quietly filling GPU memory. Past it, markers reuse whatever is
// already built — a wrong ring colour, never a leak.
const MAX_SPRITES = 96;

function rgba(hex: string, alpha: number): string {
  const h = hex.replace('#', '');
  const v = parseInt(h.length === 3 ? h.split('').map((c) => c + c).join('') : h, 16);
  if (!Number.isFinite(v)) return `rgba(255,23,68,${alpha})`;
  // eslint-disable-next-line no-bitwise
  return `rgba(${(v >> 16) & 255},${(v >> 8) & 255},${v & 255},${alpha})`;
}

// THE SPRITES ANIMATE AT THEIR OWN FRAME RATE, NOT THE DISPLAY'S. MapLibre
// calls an animated image's render() once per map frame and repaints the whole
// map whenever one reports a change — and the original sprites called
// triggerRepaint() from inside render(), which made every frame request the
// next: a self-sustaining 60 Hz redraw of the ENTIRE map (every basemap,
// tile layer and overlay, plus the compositor re-blurring every glass panel
// over the canvas) for as long as any marker on screen was fresh, which on a
// live feed is always. Each frame also fired MapLibre's `idle` event, so every
// listener waiting for the map to settle ran sixty times a second too.
//
// Now a sprite redraws only when SPRITE_FRAME_MS has passed since its last
// frame, and the next map frame is asked for by ONE timer per map instead of
// by each sprite on each frame. The ring still expands and the pulse still
// beats — both are clocked on performance.now(), not on frame count — at a
// rate the eye reads as smooth, while the map idles between them.
const SPRITE_FPS = 24;
const SPRITE_FRAME_MS = 1000 / SPRITE_FPS;
const spriteFrameTimers = new WeakMap<maplibregl.Map, number>();

function requestSpriteFrame(map: maplibregl.Map): void {
  if (spriteFrameTimers.has(map)) return;
  spriteFrameTimers.set(map, window.setTimeout(() => {
    spriteFrameTimers.delete(map);
    map.triggerRepaint();
  }, SPRITE_FRAME_MS));
}

/** The arrival ring: `ringCount` rings expanding out of the marker, evenly
 *  spread through one `ringPeriodMs` cycle. The mirror of eventstyle's
 *  ringProgress — it calls it directly, so the admin preview and the map cannot
 *  disagree about the shape of the animation. */
function makeRingSprite(map: maplibregl.Map, style: EventMarkerStyle,
                        color: string): maplibregl.StyleImageInterface {
  const pad = style.ringWidthPx + 2;
  const half = Math.ceil(style.ringMaxPx + pad);
  const size = half * 4;           // diameter, ×2 for pixelRatio 2
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d')!;
  const c = size / 2;
  let lastDrawn = -Infinity;
  const image = {
    width: size,
    height: size,
    data: new Uint8ClampedArray(size * size * 4),
    render(): boolean {
      requestSpriteFrame(map);
      const now = performance.now();
      if (now - lastDrawn < SPRITE_FRAME_MS - 1) return false;
      lastDrawn = now;
      ctx.clearRect(0, 0, size, size);
      for (let i = 0; i < style.ringCount; i += 1) {
        const p = ringProgress(style, i, now);
        ctx.beginPath();
        ctx.arc(c, c, (3 + (style.ringMaxPx - 3) * p) * 2, 0, Math.PI * 2);
        ctx.strokeStyle = rgba(color, style.ringOpacity * (1 - p));
        ctx.lineWidth = Math.max(0.6, style.ringWidthPx * (1 - p)) * 2;
        ctx.stroke();
      }
      image.data = ctx.getImageData(0, 0, size, size).data;
      return true;
    },
  };
  return image as unknown as maplibregl.StyleImageInterface;
}

/** The size pulse: the glyph itself is redrawn larger and smaller inside a
 *  fixed canvas. The canvas grows with the intensity so the peak still fits;
 *  padding does not change the glyph's apparent size, because icon-size scales
 *  the whole image and the glyph keeps its fraction of it. */
function makePulseGlyph(map: maplibregl.Map, style: EventMarkerStyle,
                        glyphChar: string, tint: string): maplibregl.StyleImageInterface {
  const box = Math.ceil(GLYPH_BOX * (1 + style.pulsePct / 100));
  const canvas = document.createElement('canvas');
  canvas.width = box;
  canvas.height = box;
  const ctx = canvas.getContext('2d')!;
  let lastDrawn = -Infinity;
  const image = {
    width: box,
    height: box,
    data: new Uint8ClampedArray(box * box * 4),
    render(): boolean {
      requestSpriteFrame(map);
      const now = performance.now();
      if (now - lastDrawn < SPRITE_FRAME_MS - 1) return false;
      lastDrawn = now;
      paintGlyph(ctx, glyphChar, box, pulseFactor(style, now), tint);
      image.data = ctx.getImageData(0, 0, box, box).data;
      return true;
    },
  };
  return image as unknown as maplibregl.StyleImageInterface;
}

/** The sprite for one marker's ring, built on first use. Returns '' when the
 *  cache is full, which the caller reads as "no ring on this one". */
function ringSprite(map: maplibregl.Map, style: EventMarkerStyle, color: string): string {
  const name = `live-ringimg-${style.ringMaxPx}-${style.ringWidthPx}-`
    + `${style.ringPeriodMs}-${style.ringCount}-${style.ringOpacity}-${color.slice(1)}`;
  if (spriteNames.has(name)) return name;
  if (spriteNames.size >= MAX_SPRITES) return '';
  spriteNames.add(name);
  if (!map.hasImage(name)) {
    map.addImage(name, makeRingSprite(map, style, color), { pixelRatio: 2 });
  }
  return name;
}

/** The sprite for one marker's glyph: static or pulsing, natural or tinted. */
function glyphSprite(map: maplibregl.Map, style: EventMarkerStyle,
                     kind: string, pulsing: boolean, tint: string): string {
  const glyphChar = (LIVE_KINDS[kind] ?? LIVE_KINDS.unclear!).glyph;
  const suffix = tint ? tint.slice(1) : 'nat';
  const name = pulsing
    ? `live-puls-${kind}-${style.pulsePct}-${style.pulsePeriodMs}-${suffix}`
    : `live-kind-${kind}-${suffix}`;
  if (spriteNames.has(name)) return name;
  if (spriteNames.size >= MAX_SPRITES) return `live-kind-${kind}-nat`;
  spriteNames.add(name);
  if (!map.hasImage(name)) {
    if (pulsing) {
      map.addImage(name, makePulseGlyph(map, style, glyphChar, tint), { pixelRatio: 2 });
    } else {
      map.addImage(name, drawLiveGlyph(glyphChar, tint), { pixelRatio: 2 });
    }
  }
  return name;
}

export function tileUrl(): string {
  return `${location.origin}/api/tiles/entities/{z}/{x}/{y}?v=${tileVersion}.${tileBust}`;
}

/** Point the entity tiles at a moment (`?asof`), or back at now with null.
 *  The replay's tile clock (timeline.ts) and a sheet export stepping through
 *  dates both go through here, so the URL has one spelling. */
export function setEntitiesAsof(map: maplibregl.Map, asof: string | null): void {
  const source = map.getSource('entities') as maplibregl.VectorTileSource | undefined;
  if (!source) return;
  const base = tileUrl();
  source.setTiles([asof ? `${base}&asof=${encodeURIComponent(asof)}` : base]);
}

// Collision priority for the symbol layer: lower sort key places first, so
// when chips collide at low zoom the mobile/scarce things (units, actors) beat
// the dense imported background (places). Within a domain the registry's own
// sort order decides.
function symbolPriority(): unknown {
  const domainRank: Record<string, number> = { object: 0, actor: 1, event: 2, place: 3 };
  const expr: unknown[] = ['match', ['get', 'type_key']];
  for (const t of registry?.types ?? []) {
    const domain = registry?.categories.find((c) => c.key === t.category)?.domain ?? 'place';
    expr.push(t.key, (domainRank[domain] ?? 3) * 1000 + t.sort);
  }
  expr.push(9999);
  return expr;
}

// Tile point features that are NOT units (they get their own treatment):
// the event-domain types, derived from the registry so new event types keep
// their special rendering without a code change.
function nonUnitTypes(): string[] {
  if (!registry) return ['live_event', 'damage_marker'];
  const eventCats = new Set(
    registry.categories.filter((c) => c.domain === 'event').map((c) => c.key));
  return registry.types.filter((t) => eventCats.has(t.category)).map((t) => t.key);
}

// The client-side live-event layers, as data. Extracted from installObjectLayers
// so a TEST can validate them against the MapLibre style spec: every expression
// here is checked only when addLayer runs, and a single malformed one throws
// during map construction and takes the WHOLE map down — an all-or-nothing
// failure that no amount of careful reading reliably catches. See
// objects.test.ts, which runs validateStyleMin over exactly this list.

// The zoom ramp is the OUTER expression with the per-feature part inside each
// stop, here and in every layer below. MapLibre rejects `zoom` anywhere but as
// the input to a top-level interpolate/step, so the intuitive
// `['*', ['get', …], ['interpolate', …, ['zoom'], …]]` throws at addLayer.
const glyphSize: unknown = ['interpolate', ['linear'], ['zoom'],
  4, ['*', ['to-number', ['get', 'symSize']], 0.62],
  12, ['*', ['to-number', ['get', 'symSize']], 0.95]];
// Centre-anchored with an explicit offset, NOT anchor:'bottom': a pulsing
// sprite lives on a bigger canvas than a static one, so a bottom anchor would
// jog the glyph upward the instant the pulse ended. The offset lifts it clear
// of the dot, or sits it on the point when there is no dot to clear.
const glyphOffset: unknown =
  ['case', ['==', ['get', 'dot'], true], ['literal', [0, -16]], ['literal', [0, 0]]];

export function liveEventLayers(): unknown[] {
  return [
  // Replay splash: a ring that appears where an event lands as the playhead
  // crosses its occurrence, expanding and fading (`p` = 0..1 progress, baked
  // per tick). Only populated during playback — overlapping rings are the
  // point, they show where a burst of activity concentrates.
  {
    id: 'live-splash', type: 'circle', source: SPLASH,
    paint: {
      'circle-color': 'rgba(0,0,0,0)',
      'circle-radius': ['interpolate', ['linear'], ['get', 'p'], 0, 5, 1, 48] as never,
      // Operator colour rules (faction / attack-vs-impact) ride the ring.
      'circle-stroke-color': ['coalesce', ['get', 'color'], '#ef4444'] as never,
      'circle-stroke-width': ['interpolate', ['linear'], ['get', 'p'], 0, 3, 1, 0.8] as never,
      'circle-stroke-opacity': ['interpolate', ['linear'], ['get', 'p'], 0, 0.95, 1, 0] as never,
      'circle-pitch-alignment': 'map',
    },
  },

  // Legs from a fanned-out marker to the point it was moved off. Several
  // situations geocoded to the same name land on one coordinate; the bake
  // spreads them (spread.ts) and the legs converging on the shared point are
  // what says "these are all HERE", so the fan reads as one place with many
  // markers rather than five events scattered around a town.
  {
    id: 'live-leg', type: 'line', source: LEGS,
    paint: {
      'line-color': ['get', 'color'] as never,
      'line-width': 1.2,
      'line-opacity': ['*', 0.7, ['to-number', ['get', 'opacity']]] as never,
    },
  },

  // Live events: age-coloured dots from the client (tiles don't carry time).
  // Launches get their own animated marker, so they drop out of the dot/halo.
  {
    id: 'live-halo', type: 'circle', source: LIVE,
    filter: ['==', ['get', 'halo'], true],
    paint: {
      // Faction / class colour rules win the halo; the marker's own colour
      // (stage ramp, else age ramp) is the default. Both are resolved in the
      // bake, so the layer reads one property instead of a coalesce chain.
      'circle-color': ['get', 'haloColor'] as never,
      // The zoom ramp has to be the OUTER expression with the data-driven part
      // inside each stop: MapLibre rejects `zoom` anywhere but as the input to
      // a top-level interpolate/step, so the intuitive `['*', get, interpolate]`
      // throws at addLayer and takes the whole map with it.
      'circle-radius': ['interpolate', ['linear'], ['zoom'],
        4, ['*', ['to-number', ['get', 'haloR']], 0.72],
        10, ['to-number', ['get', 'haloR']]] as never,
      'circle-opacity': ['*', ['to-number', ['get', 'haloOpacity']],
        ['get', 'opacity']] as never,
    },
  },
  // The arrival ring: a fresh event announces itself like a lightning tracker,
  // so "something just happened" is findable from across the room. Which sprite
  // (size, colour, speed, count) is the operator's choice — see eventstyle.ts.
  {
    id: 'live-ring', type: 'symbol', source: LIVE,
    filter: ['==', ['get', 'flash'], true],
    layout: {
      'icon-image': ['get', 'ringImg'] as never,
      // The sprite already carries its size in screen pixels; this only pulls
      // it in a little at theatre zoom, where a 26 px ring covers a province.
      'icon-size': ['interpolate', ['linear'], ['zoom'], 4, 0.75, 10, 1] as never,
      'icon-allow-overlap': true,
      'icon-ignore-placement': true,
    },
    paint: { 'icon-opacity': ['get', 'opacity'] as never },
  },
  {
    id: 'live-dot', type: 'circle', source: LIVE,
    filter: ['==', ['get', 'dot'], true],
    paint: {
      'circle-color': ['get', 'fillColor'] as never,
      'circle-radius': ['interpolate', ['linear'], ['zoom'],
        4, ['*', ['to-number', ['get', 'dotR']], 0.72],
        12, ['to-number', ['get', 'dotR']]] as never,
      'circle-opacity': ['get', 'opacity'] as never,
      // The dot FILL stays the age ramp (how old); the STROKE takes the
      // operator's faction/class colour when one is set (who / what).
      'circle-stroke-color': ['coalesce', ['get', 'ring'], 'rgba(255,255,255,.9)'] as never,
      'circle-stroke-width': ['case', ['!=', ['coalesce', ['get', 'ring'], ''], ''], 2.2, 1.4] as never,
      'circle-stroke-opacity': ['get', 'opacity'] as never,
    },
  },
  // Kind glyphs above the dots. Collision-managed (no allow-overlap): in a
  // dense cluster only some glyphs place, but the dots underneath always
  // render, so density stays readable and kind detail appears as you zoom.
  {
    id: 'live-glyph', type: 'symbol', source: LIVE,
    filter: ['all', ['==', ['get', 'symbol'], true], ['!=', ['get', 'isNew'], true]],
    layout: {
      'icon-image': ['get', 'glyphImg'] as never,
      // How many reports this marker stands for. Only ever drawn for a real
      // group: an empty string renders nothing, so a lone event is untouched.
      'text-field': ['case', ['>', ['get', 'count'], 1],
                     ['to-string', ['get', 'count']], ''] as never,
      'text-size': 10,
      'text-offset': [0.95, -0.95],
      'text-anchor': 'center',
      'text-allow-overlap': true,
      'text-ignore-placement': true,      'icon-anchor': 'center',
      'icon-offset': glyphOffset as never,
      'icon-size': glyphSize as never,
    },
    paint: { 'icon-opacity': ['get', 'opacity'] as never },
  },
  // New events' glyphs bypass collision management: seeing WHAT just happened
  // must not depend on the label lottery in a dense cluster. Older events keep
  // the managed layer above, so density stays readable.
  {
    id: 'live-glyph-new', type: 'symbol', source: LIVE,
    filter: ['all', ['==', ['get', 'symbol'], true], ['==', ['get', 'isNew'], true]],
    layout: {
      'icon-image': ['get', 'glyphImg'] as never,
      'icon-anchor': 'center',
      'icon-offset': glyphOffset as never,
      'icon-size': glyphSize as never,
      'icon-allow-overlap': true,
      'icon-ignore-placement': true,
    },
    paint: { 'icon-opacity': ['get', 'opacity'] as never },
  },
  ];
}

export function installObjectLayers(map: maplibregl.Map): void {
  const NON_UNIT_TYPES = nonUnitTypes();
  // The sprite cache is keyed by name only, so it has to be emptied for each
  // new map — renderApp builds a fresh one on every login, and a name the old
  // map registered would otherwise be skipped as "already built" and leave the
  // new map pointing at an image that does not exist on it.
  spriteNames.clear();
  appliedFactionColors = null;   // same reason: the memo describes the OLD map
  coverageSig = null;
  map.addSource(SRC, { type: 'vector', tiles: [tileUrl()], minzoom: 0, maxzoom: 18 });
  map.addSource(SEL, { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
  map.addSource(COVERAGE, {
    type: 'geojson', data: { type: 'FeatureCollection', features: [] },
  });
  map.addSource(LIVE, {
    type: 'geojson', data: { type: 'FeatureCollection', features: [] },
  });
  map.addSource(SPLASH, {
    type: 'geojson', data: { type: 'FeatureCollection', features: [] },
  });
  map.addSource(LEGS, {
    type: 'geojson', data: { type: 'FeatureCollection', features: [] },
  });
  installMarkerImages(map);
  // The fan that separates stacked markers is a fixed size ON SCREEN, so its
  // geographic offsets are a function of zoom: re-bake as the zoom settles
  // (throttled mid-gesture, and once more at the end so the final picture is
  // exact). A pan never changes what stacks, so `move` is not listened to.
  let zoomBake = 0;
  map.on('zoom', () => {
    if (zoomBake) return;
    zoomBake = window.setTimeout(() => { zoomBake = 0; bakeLiveDots(map); }, 150);
  });
  map.on('zoomend', () => bakeLiveDots(map));

  const color = colorMatch('#4f7fd9');

  // Coverage: engagement = solid + faint fill; radar = dashed, no fill.
  map.addLayer({
    id: 'coverage-fill', type: 'fill', source: COVERAGE,
    filter: ['==', ['get', 'kind'], 'engage'],
    paint: {
      'fill-color': ['get', 'color'] as never,
      'fill-opacity': ['case', ['==', ['get', 'status'], 'offline'], 0.015, 0.055] as never,
    },
  });
  map.addLayer({
    id: 'coverage-engage', type: 'line', source: COVERAGE,
    filter: ['==', ['get', 'kind'], 'engage'],
    paint: {
      'line-color': ['get', 'color'] as never,
      'line-width': ['interpolate', ['linear'], ['zoom'], 3, 0.9, 9, 1.6] as never,
      'line-opacity': ['case', ['==', ['get', 'status'], 'offline'], 0.22, 0.65] as never,
    },
  });
  map.addLayer({
    id: 'coverage-radar', type: 'line', source: COVERAGE,
    filter: ['==', ['get', 'kind'], 'radar'],
    paint: {
      'line-color': ['get', 'color'] as never,
      'line-width': ['interpolate', ['linear'], ['zoom'], 3, 0.8, 9, 1.2] as never,
      'line-opacity': ['case', ['==', ['get', 'status'], 'offline'], 0.12, 0.4] as never,
      'line-dasharray': [2, 3],
    },
  });

  // Territorial control first: ground goes under everything drawn on it. A
  // contested hatch whose faction has no pattern yet (colours arrive with the
  // entity list) draws in the fallback colour rather than nothing.
  for (const layer of controlLayers()) map.addLayer(layer as never);
  map.on('styleimagemissing', (e) => {
    if (e.id.startsWith('hatch-') && !map.hasImage(e.id)) {
      map.addImage(e.id, drawHatch(CONTROL_FALLBACK), { pixelRatio: 2 });
    }
  });

  map.addLayer({
    id: 'entities-fill', type: 'fill', source: SRC, 'source-layer': 'polygons',
    filter: NOT_CONTROL as never,
    paint: { 'fill-color': color as never, 'fill-opacity': 0.1 },
  });
  map.addLayer({
    id: 'entities-outline', type: 'line', source: SRC, 'source-layer': 'polygons',
    filter: NOT_CONTROL as never,
    paint: { 'line-color': color as never, 'line-width': 1.3 },
  });
  map.addLayer({
    id: 'entities-line', type: 'line', source: SRC, 'source-layer': 'lines',
    paint: { 'line-color': color as never, 'line-width': 2 },
  });

  // Damage markers first: quiet, small, below everything unit-like. They do
  // NOT pulse when new — tried and reverted: the pulse is the "happening NOW"
  // signal, and damage is a record of something already over, dated by
  // imagery. Events pulse; damage sits still.
  map.addLayer({
    id: 'entities-damage', type: 'symbol', source: SRC, 'source-layer': 'points',
    filter: ['==', ['get', 'type_key'], 'damage_marker'],
    layout: {
      'icon-image': 'entity-damage_marker',
      'icon-size': ['interpolate', ['linear'], ['zoom'], 4, 0.55, 10, 0.8, 14, 1] as never,
      'icon-allow-overlap': true,
      'icon-ignore-placement': true,
    },
    paint: { 'icon-opacity': 0.85 },
  });

  // Status ring behind the chip: readiness at a glance.
  map.addLayer({
    id: 'entities-ring', type: 'circle', source: SRC, 'source-layer': 'points',
    filter: ['all',
      ['!', ['in', ['get', 'type_key'], ['literal', NON_UNIT_TYPES]]],
      ['in', ['coalesce', ['get', 'status'], ''], ['literal', Object.keys(STATUS_COLORS)]]],
    paint: {
      'circle-color': 'rgba(0,0,0,0)',
      'circle-radius': ['interpolate', ['linear'], ['zoom'], 4, 10, 10, 13, 15, 15] as never,
      'circle-stroke-width': 2,
      'circle-stroke-color': ['match', ['coalesce', ['get', 'status'], ''],
        'ready', STATUS_COLORS.ready!, 'limited', STATUS_COLORS.limited!,
        'offline', STATUS_COLORS.offline!, 'rgba(0,0,0,0)'] as never,
      'circle-stroke-opacity': 0.9,
    },
  });
  map.addLayer({
    id: 'entities-symbol', type: 'symbol', source: SRC, 'source-layer': 'points',
    filter: ['!', ['in', ['get', 'type_key'], ['literal', NON_UNIT_TYPES]]],
    layout: {
      'icon-image': ['concat', 'entity-', ['get', 'type_key']] as never,
      'icon-size': ['interpolate', ['linear'], ['zoom'], 4, 0.55, 10, 0.75, 15, 0.9] as never,
      // Collision-culled, not always-on: with thousands of facilities loaded,
      // unconditional overlap painted every sprite every frame and turned low
      // zooms into an unreadable smear. Sort key decides who wins a collision:
      // units first, then places; zooming in reveals the rest.
      'icon-overlap': 'never',
      'symbol-sort-key': symbolPriority() as never,
    },
  });
  map.addLayer({
    id: 'entities-label', type: 'symbol', source: SRC, 'source-layer': 'points',
    minzoom: 8,
    filter: ['!', ['in', ['get', 'type_key'], ['literal', NON_UNIT_TYPES]]],
    layout: {
      'text-field': ['get', 'name'] as never,
      'text-font': ['Noto Sans Bold'],   // OpenFreeMap serves Noto only; Open Sans 404s
      'text-size': ['interpolate', ['linear'], ['zoom'], 8, 10, 12, 11.5] as never,
      'text-offset': [0, 1.5],
      'text-anchor': 'top',
      'text-optional': true,
    },
    paint: {
      'text-color': '#123034',
      'text-halo-color': 'rgba(255,255,255,.88)',
      'text-halo-width': 1.4,
    },
  });

  for (const layer of liveEventLayers()) {
    map.addLayer(layer as never);
  }
  // Static natural-colour glyph sprites for every kind; tinted and pulsing
  // variants are minted on demand by the bake.
  for (const k of Object.keys(LIVE_KINDS)) glyphSprite(map, DEFAULT_MARKER_STYLE, k, false, '');

  // Selected/edited objects render instantly on top from the client source.
  // Ring features here come from rangeRingFeatures — the same builder as the
  // reference COVERAGE layer above — hence the shared 'engage'/'radar' kinds.
  map.addLayer({
    id: 'selected-buffer-fill', type: 'fill', source: SEL,
    filter: ['==', ['get', 'kind'], 'engage'],
    paint: { 'fill-color': ['get', 'color'] as never, 'fill-opacity': 0.06 },
  });
  map.addLayer({
    id: 'selected-buffer-line', type: 'line', source: SEL,
    filter: ['==', ['get', 'kind'], 'engage'],
    paint: { 'line-color': ['get', 'color'] as never, 'line-width': 1.6 },
  });
  map.addLayer({
    id: 'selected-radar-line', type: 'line', source: SEL,
    filter: ['==', ['get', 'kind'], 'radar'],
    paint: {
      'line-color': ['get', 'color'] as never, 'line-width': 1.2,
      'line-dasharray': [2, 3], 'line-opacity': 0.6,
    },
  });
  map.addLayer({
    id: 'selected-halo', type: 'circle', source: SEL,
    filter: ['all', ['==', ['geometry-type'], 'Point'],
             ['!', ['has', 'kind']]],
    paint: {
      'circle-color': '#ffd60a', 'circle-radius': 13, 'circle-opacity': 0.3,
      'circle-stroke-color': '#ffd60a', 'circle-stroke-width': 2,
    },
  });
}

// Map labels must flip with the app theme — paint can't read CSS variables.
export function applyMapTheme(map: maplibregl.Map, dark: boolean): void {
  if (!map.getLayer('entities-label')) return;
  map.setPaintProperty('entities-label', 'text-color',
    dark ? '#dff6f1' : '#123034');
  map.setPaintProperty('entities-label', 'text-halo-color',
    dark ? 'rgba(7,18,25,.85)' : 'rgba(255,255,255,.88)');
}

// Geodesic circle: `km` radius around [lon, lat], 72 segments. Used for
// semantic attributes (range_km etc.) that render as geometry.
export function geodesicCircle(lon: number, lat: number, km: number):
    [number, number][] {
  const R = 6371.0088;
  const d = km / R;
  const lat1 = (lat * Math.PI) / 180;
  const lon1 = (lon * Math.PI) / 180;
  const ring: [number, number][] = [];
  for (let i = 0; i <= 72; i++) {
    const brg = (i / 72) * 2 * Math.PI;
    const lat2 = Math.asin(Math.sin(lat1) * Math.cos(d)
      + Math.cos(lat1) * Math.sin(d) * Math.cos(brg));
    const lon2 = lon1 + Math.atan2(
      Math.sin(brg) * Math.sin(d) * Math.cos(lat1),
      Math.cos(d) - Math.sin(lat1) * Math.sin(lat2));
    ring.push([(lon2 * 180) / Math.PI, (lat2 * 180) / Math.PI]);
  }
  return ring;
}

// Geodesic sector: a wedge of `azimuthDeg` centred on compass `bearingDeg`
// (0 = north, clockwise), as a closed polygon (site → arc → site). A fixed-face
// phased-array radar sees only a sector, not a full disc — this is that
// footprint. A full 360° reduces to the plain circle.
export function geodesicSector(lon: number, lat: number, km: number,
                               azimuthDeg: number, bearingDeg: number):
    [number, number][] {
  if (azimuthDeg >= 360) return geodesicCircle(lon, lat, km);
  const R = 6371.0088;
  const d = km / R;
  const lat1 = (lat * Math.PI) / 180;
  const lon1 = (lon * Math.PI) / 180;
  const half = azimuthDeg / 2;
  const steps = Math.max(8, Math.round((72 * azimuthDeg) / 360));
  const ring: [number, number][] = [[lon, lat]];
  for (let i = 0; i <= steps; i++) {
    const brg = ((bearingDeg - half + (azimuthDeg * i) / steps) * Math.PI) / 180;
    const lat2 = Math.asin(Math.sin(lat1) * Math.cos(d)
      + Math.cos(lat1) * Math.sin(d) * Math.cos(brg));
    const lon2 = lon1 + Math.atan2(
      Math.sin(brg) * Math.sin(d) * Math.cos(lat1),
      Math.cos(d) - Math.sin(lat1) * Math.sin(lat2));
    ring.push([(lon2 * 180) / Math.PI, (lat2 * 180) / Math.PI]);
  }
  ring.push([lon, lat]);
  return ring;
}

export type CategoryVisibility = Record<string, boolean>;

// Damage markers pile up fast; by default the map shows only the recent ones.
export const DAMAGE_RECENT_DAYS = 3;

// Default zoom gate for place types without their own style.minZoom — the
// chips are collision-culled (units win the slots, places fill in as space
// allows), but below z10 the thousands of OSM-imported sites still crowd out
// more than they add. Admins override per type in the Unit types tab; watched
// places are the standing exception and render at every zoom (see
// applyObjectFilter).
export const PLACE_MINZOOM = 10;

// The zoom each type's features first draw at, as a match expression over
// type_key. An explicit style.minZoom (admin-set) wins; place-domain types
// default to PLACE_MINZOOM; everything else draws at every zoom.
function typeMinZoomExpr(): unknown {
  const placeCats = new Set((registry?.categories ?? [])
    .filter((c) => c.domain === 'place').map((c) => c.key));
  const expr: unknown[] = ['match', ['get', 'type_key']];
  for (const t of registry?.types ?? []) {
    const mz = typeof t.style.minZoom === 'number'
      ? t.style.minZoom
      : placeCats.has(t.category) ? PLACE_MINZOOM : 0;
    if (mz > 0) expr.push(t.key, mz);
  }
  expr.push(0);
  // A match needs at least one branch; with no gated types, no gate at all.
  return expr.length > 3 ? expr : 0;
}

/** A date window in epoch ms; a null edge is unbounded. */
export interface TimeRange { fromMs: number | null; toMs: number | null }

export interface ObjectFilterState {
  categories: CategoryVisibility;
  /** Which damage markers draw. They age by their REPORTED date — mvt_entities
   *  stamps `ts` from attrs.date for this type alone (migration 010) — so this
   *  is "hit between these dates", not "recorded between them". */
  damageRange?: TimeRange | null;
  /** null = every type visible; otherwise only these type keys draw. */
  types?: string[] | null;
  /** Hide place-domain features that aren't watched for imagery. */
  watchedOnly?: boolean;
  /** Show only features matching this country; features that never carried a
   *  country (units, hand-placed markers) stay visible rather than vanishing. */
  country?: string | null;
}

export function applyObjectFilter(map: maplibregl.Map, state: ObjectFilterState): void {
  // Mid-replay the panels keep asking; record what they want and hold the
  // replay view — setPlaybackView(false) replays the latest ask on exit.
  lastFilterState = state;
  if (playbackView) return;
  const visible = state.categories;
  const enabled = Object.entries(visible).filter(([, on]) => on).map(([k]) => k);
  const cat = ['in', ['get', 'category_key'], ['literal', enabled]];
  const notUnit = ['in', ['get', 'type_key'], ['literal', nonUnitTypes()]];

  // Narrowing filters shared by every entity layer. `watched`/`country` are
  // tile properties (migration 022): effective watch state and best-effort
  // country label ("Iran", possibly ";"-joined — hence the substring `in`).
  const placeCats = (registry?.categories ?? [])
    .filter((c) => c.domain === 'place').map((c) => c.key);
  const isPlace = ['in', ['get', 'category_key'], ['literal', placeCats]];
  const extra: unknown[] = [];
  if (state.types) {
    extra.push(['in', ['get', 'type_key'], ['literal', state.types]]);
  }
  // Story scope. `story` is a tile PROPERTY (migration 028): the uuid the
  // version is filed under, '' when unfiled. Never a tile query parameter —
  // tenancy is already enforced server-side inside can_read, so a story this
  // client cannot see never reaches its tiles and this half is display only,
  // and keeping it out of tileUrl() keeps one URL meaning one set of bytes,
  // which is what Martin's cache and the asyncpg fallback both rely on.
  // The ARRAY form of `in` is exact membership, not the substring test that
  // `country` below relies on, so one uuid can never match another's prefix.
  // coalesce, like `status` above: a tile cached from before 028 carries no
  // `story` at all, and a null needle is an expression ERROR (the feature
  // drops with a console warning), not a miss.
  const subtree = activeSubtreeIds();
  const storyProp = ['coalesce', ['get', 'story'], ''];
  const inStory: unknown[] = ['in', storyProp, ['literal', subtree]];
  if (subtree.length) {
    // Unfiled is universal: filing narrows, unfiling widens. Unfiled features
    // stay on the map under every selection — nothing is filed on day one and
    // hiding them would read as a broken feature — unless the operator asked
    // for this story alone (the Layers pod's "Only this story").
    extra.push(isOnlyThisStory()
      ? inStory
      : ['any', ['==', storyProp, ''], inStory]);
  }
  // Zoom-gate by type: a feature draws once past its type's min zoom
  // (typeMinZoomExpr — admin-set per type, place types defaulting to
  // PLACE_MINZOOM, everything else 0), or at any zoom when it is watched
  // (few, and the ones that matter — always visible). A member of the selected
  // story is the same kind of exception: a story is a small, deliberate
  // selection, and without this arm one made of place-domain entities renders
  // an EMPTY MAP at theatre zoom, since place types default to PLACE_MINZOOM.
  // A `zoom` term makes this a MapLibre dynamic filter, re-evaluated as you zoom.
  extra.push(['any',
    ['==', ['get', 'watched'], true],
    ...(subtree.length ? [inStory] : []),
    ['>=', ['zoom'], typeMinZoomExpr()]]);
  if (state.watchedOnly) {
    extra.push(['any', ['!', isPlace], ['==', ['get', 'watched'], true]]);
  }
  if (state.country) {
    extra.push(['any', ['!', ['has', 'country']],
      ['in', state.country, ['get', 'country']]]);
  }

  for (const id of ['entities-fill', 'entities-outline', 'entities-line']) {
    map.setFilter(id, ['all', NOT_CONTROL, cat, ...extra] as never);
  }
  // Territories take the same narrowing (category toggle, story scope,
  // country) on top of their own standing filters. The zoom gate in `extra`
  // is satisfied at every zoom — the territory type declares minZoom 0.
  for (const id of CONTROL_LAYER_IDS) {
    if (map.getLayer(id)) {
      map.setFilter(id, ['all', CONTROL_BASE_FILTERS[id], cat, ...extra] as never);
    }
  }
  for (const id of ['entities-ring', 'entities-symbol', 'entities-label']) {
    const base = id === 'entities-ring'
      ? ['in', ['coalesce', ['get', 'status'], ''], ['literal', Object.keys(STATUS_COLORS)]]
      : true;
    map.setFilter(id, ['all', ['!', notUnit], cat, base, ...extra] as never);
  }
  const damageFilter: unknown[] =
    ['all', ['==', ['get', 'type_key'], 'damage_marker'], cat, ...extra];
  const dr = state.damageRange;
  if (dr && (dr.fromMs != null || dr.toMs != null)) {
    // ts is epoch seconds (mvt_entities). A feature without ts (stale tile)
    // stays visible rather than vanishing until the next tile refresh.
    const within: unknown[] = ['all'];
    if (dr.fromMs != null) {
      within.push(['>=', ['get', 'ts'], Math.floor(dr.fromMs / 1000)]);
    }
    if (dr.toMs != null) {
      within.push(['<=', ['get', 'ts'], Math.floor(dr.toMs / 1000)]);
    }
    damageFilter.push(['any', ['!', ['has', 'ts']], within]);
  }
  map.setFilter('entities-damage', damageFilter as never);
  // Live-event dots are client-side; they follow the Events category toggle
  // (the live_event type checkbox also folds in, so "hide live events" works
  // from either the category or the type list).
  const liveVisible = visible['event'] !== false
    && !(state.types && !state.types.includes('live_event'));
  for (const id of LIVE_LAYER_IDS) {
    if (id === 'live-splash') continue;   // replay-only; the transport owns it
    if (map.getLayer(id)) {
      map.setLayoutProperty(id, 'visibility', liveVisible ? 'visible' : 'none');
    }
  }
}

// Only an explicit range_km is an engagement envelope. A bare `range` is
// ambiguous — on a truck it is fuel range, and treating it as a weapons
// envelope drew a 500 km "air defence" ring around a pickup near Riyadh.
export function rangeKmOf(entity: EntityDto): number | null {
  const value = entity.attrs['range_km'];
  if (typeof value === 'number' && Number.isFinite(value) && value > 0) return value;
  if (typeof value === 'string' && /^\d+(\.\d+)?$/.test(value)) return Number(value);
  return null;
}

export function radarRangeKmOf(entity: EntityDto): number | null {
  const value = entity.attrs['radar_range_km'];
  if (typeof value === 'number' && Number.isFinite(value) && value > 0) return value;
  if (typeof value === 'string' && /^\d+(\.\d+)?$/.test(value)) return Number(value);
  const fallback = typeDefaultNum(entity, 'radar_range_km');
  return fallback !== null && fallback > 0 ? fallback : null;
}

// A real radar's coverage is an altitude-dependent shell, not a hemisphere or
// cylinder. These field-of-regard parameters combine with sensor altitude,
// effective-Earth horizon, and slant range in radar.ts; a fixed-face phased
// array also narrows both the 2D reference ring and 3D shell to a sector.
// Values come from the entity, then the type's schema default, then a fallback
// — so a seeded sector-radar type draws as a wedge the instant it's placed,
// before anyone opens the edit form.
export interface RadarShape { maxElevDeg: number; azimuthDeg: number; bearingDeg: number }

export interface EntityRadarModel {
  spec: RadarSpec;
  /** Airborne: altitude above datum. Ground/naval/aerostat: height above the
   *  local surface, promoted to datum altitude by the renderer/terrain service. */
  pose: RadarPose;
  /** Optional surveyed/modelled site elevation used when no DEM sample exists. */
  siteElevationKm?: number;
  effectiveEarthK: number;
}

function typeDefaultNum(entity: EntityDto, key: string): number | null {
  const props = (registry?.types.find((t) => t.key === entity.type)?.attrSchema as
    { properties?: Record<string, { default?: unknown }> } | undefined)?.properties;
  const d = props?.[key]?.default;
  return typeof d === 'number' && Number.isFinite(d) ? d : null;
}

function entityAttrNum(entity: EntityDto, key: string): number | null {
  const value = entity.attrs[key];
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && /^-?\d+(\.\d+)?$/.test(value)) return Number(value);
  return null;
}

function attrOrTypeNum(entity: EntityDto, key: string, fallback: number): number {
  return entityAttrNum(entity, key) ?? typeDefaultNum(entity, key) ?? fallback;
}

function optionalAttrOrTypeNum(entity: EntityDto, key: string): number | undefined {
  return entityAttrNum(entity, key) ?? typeDefaultNum(entity, key) ?? undefined;
}

function attrOrTypeString(entity: EntityDto, key: string): string | undefined {
  const v = entity.attrs[key];
  if (typeof v === 'string' && v.trim()) return v.trim();
  const props = (registry?.types.find((t) => t.key === entity.type)?.attrSchema as
    { properties?: Record<string, { default?: unknown }> } | undefined)?.properties;
  const d = props?.[key]?.default;
  return typeof d === 'string' && d.trim() ? d.trim() : undefined;
}

function radarPlatformOf(entity: EntityDto): RadarPlatform {
  const explicit = attrOrTypeString(entity, 'radar_platform');
  if (explicit === 'ground' || explicit === 'naval'
      || explicit === 'airborne' || explicit === 'aerostat') return explicit;
  const category = registry?.types.find((t) => t.key === entity.type)?.category;
  if (category === 'aircraft') return 'airborne';
  if (category === 'naval') return 'naval';
  return 'ground';
}

/** Translate data-driven entity attributes into the pure radar model. Legacy
 *  entities need only radar_range_km; every other value can arrive later from
 *  the type schema or the entity without changing renderer/simulation code. */
export function radarModelOf(entity: EntityDto): EntityRadarModel | null {
  const referenceRangeKm = radarRangeKmOf(entity);
  if (referenceRangeKm === null || entity.geometry?.type !== 'Point') return null;
  const [lon, lat] = entity.geometry.coordinates as [number, number];
  const platform = radarPlatformOf(entity);
  const altitudeFallback = platform === 'airborne' ? 9
    : platform === 'aerostat' ? 3.048 : platform === 'naval' ? 0.025 : 0.02;
  const minElevFallback = platform === 'airborne' ? -80
    : platform === 'aerostat' ? -60 : -1;
  const shape = radarShapeOf(entity);
  // Preserve explicitly edited legacy ceilings during the transition to the
  // radar-specific field. Schema defaults come only after entity attributes.
  const targetCeilingKm = entityAttrNum(entity, 'radar_ceiling_km')
    ?? entityAttrNum(entity, 'ceiling_km')
    ?? typeDefaultNum(entity, 'radar_ceiling_km')
    ?? typeDefaultNum(entity, 'ceiling_km')
    ?? 30;
  return {
    spec: {
      platform,
      referenceRangeKm,
      instrumentedRangeKm: attrOrTypeNum(entity, 'instrumented_range_km', referenceRangeKm),
      minRangeKm: Math.max(0, attrOrTypeNum(entity, 'min_range_km', 0)),
      minElevationDeg: Math.min(90, Math.max(-90,
        attrOrTypeNum(entity, 'min_elev_deg', minElevFallback))),
      maxElevationDeg: shape.maxElevDeg,
      azimuthDeg: shape.azimuthDeg,
      bearingDeg: shape.bearingDeg,
      targetCeilingKm: Math.max(0.1, targetCeilingKm),
      minTargetAltitudeKm: Math.max(0, attrOrTypeNum(entity, 'min_target_altitude_km', 0)),
      referenceRcsM2: optionalAttrOrTypeNum(entity, 'reference_rcs_m2'),
      rangeResolutionM: optionalAttrOrTypeNum(entity, 'range_resolution_m'),
      angularResolutionDeg: optionalAttrOrTypeNum(entity, 'angular_resolution_deg'),
      scanIntervalS: optionalAttrOrTypeNum(entity, 'scan_interval_s'),
    },
    pose: {
      lon, lat,
      altitudeKm: Math.max(0, attrOrTypeNum(entity, 'sensor_altitude_km', altitudeFallback)),
      headingDeg: optionalAttrOrTypeNum(entity, 'heading_deg'),
    },
    siteElevationKm: optionalAttrOrTypeNum(entity, 'site_elevation_km'),
    effectiveEarthK: Math.max(0.1, attrOrTypeNum(entity, 'refraction_k', 4 / 3)),
  };
}

export function radarShapeOf(entity: EntityDto): RadarShape {
  const platform = radarPlatformOf(entity);
  const maxElevFallback = platform === 'airborne' || platform === 'aerostat' ? 30 : 60;
  return {
    // A true 90° field of regard is allowed; the pure model handles its limit.
    maxElevDeg: Math.min(90, Math.max(-89,
      attrOrTypeNum(entity, 'max_elev_deg', maxElevFallback))),
    azimuthDeg: Math.min(360, Math.max(1, attrOrTypeNum(entity, 'azimuth_deg', 360))),
    bearingDeg: ((attrOrTypeNum(entity, 'bearing_deg', 0) % 360) + 360) % 360,
  };
}

function ringFeature(entity: EntityDto, km: number, kind: 'engage' | 'radar'): Feature {
  const [lon, lat] = (entity.geometry as Point).coordinates as [number, number];
  const type = registry?.types.find((item) => item.key === entity.type);
  const radar = kind === 'radar' ? radarModelOf(entity) : null;
  const ring = radar
    ? geodesicSector(lon, lat, km, radar.spec.azimuthDeg,
                     normalizeBearing(radar.spec.bearingDeg + (radar.pose.headingDeg ?? 0)))
    : geodesicCircle(lon, lat, km);
  return {
    type: 'Feature',
    geometry: { type: 'Polygon', coordinates: [ring] },
    properties: {
      id: entity.id,
      kind,
      status: entity.attrs.status ?? 'unknown',
      color: type?.style.color ?? '#55d6be',
    },
  };
}

/** The 2D nominal rings for one ranged unit — the single builder behind both
 *  the always-on reference layer and the selection card's "Show range rings",
 *  so the two displays can never drift apart. */
export function rangeRingFeatures(entity: EntityDto): Feature[] {
  if (entity.geometry?.type !== 'Point') return [];
  const features: Feature[] = [];
  const engage = rangeKmOf(entity);
  const radar = radarRangeKmOf(entity);
  if (engage !== null) features.push(ringFeature(entity, engage, 'engage'));
  if (radar !== null) features.push(ringFeature(entity, radar, 'radar'));
  return features;
}

/** What the coverage source was last built from: the ranged entities' ids and
 *  revs plus the toggle. Rebuilding the rings means re-tiling every polygon in
 *  the worker and re-uploading them, and this runs on every poll — for a set
 *  of units that changes rarely. */
let coverageSig: string | null = null;

export function setCoverageEntities(map: maplibregl.Map, entities: EntityDto[],
                                    enabled: boolean): number {
  const source = map.getSource(COVERAGE) as maplibregl.GeoJSONSource | undefined;
  const ranged = entities.filter((entity) =>
    entity.geometry?.type === 'Point'
    && (rangeKmOf(entity) !== null || radarRangeKmOf(entity) !== null));
  if (!source) return ranged.length;
  const sig = enabled ? ranged.map((e) => `${e.id}:${e.rev}`).join('|') : '';
  if (sig === coverageSig) return ranged.length;
  const features: Feature[] = enabled
    ? ranged.flatMap((entity) => rangeRingFeatures(entity))
    : [];
  source.setData({ type: 'FeatureCollection', features });
  coverageSig = sig;
  return ranged.length;
}

// While an entity is selected, its rings are drawn (and toggled per-unit) by the
// selection card. Drop that one entity from the always-on reference layer so the
// two don't stack into a doubled, independently-controlled ring. Pass null to
// clear the exclusion (deselect). Layer filters persist across source setData,
// so this survives coverage rebuilds without re-application.
export function setCoverageExclusion(map: maplibregl.Map, id: string | null): void {
  const guard = (kind: 'engage' | 'radar') => (id
    ? ['all', ['==', ['get', 'kind'], kind], ['!=', ['get', 'id'], id]]
    : ['==', ['get', 'kind'], kind]) as never;
  for (const [layer, kind] of [
    ['coverage-fill', 'engage'], ['coverage-engage', 'engage'],
    ['coverage-radar', 'radar'],
  ] as const) {
    if (map.getLayer(layer)) map.setFilter(layer, guard(kind));
  }
}

// Everything the panes load (places, objects, live events) is a full EntityDto
// already in memory — so a click can paint its card instantly instead of
// waiting on a fetch. The single-entity GET still runs in the background to
// fill in relations (edges) and catch a concurrent edit.
const ENTITY_CACHE = new Map<string, EntityDto>();

export function cacheEntities(list: EntityDto[]): void {
  for (const e of list) ENTITY_CACHE.set(e.id, e);
}

export function uncacheEntities(ids: string[]): void {
  for (const id of ids) ENTITY_CACHE.delete(id);
}

export function cachedEntity(id: string): EntityDto | undefined {
  return ENTITY_CACHE.get(id);
}

/** Every cached entity of one type — the marker card's "nearby reports"
 *  mirror reads live events from here, the same in-memory set the feed's
 *  corroboration chip reads markers from. A linear scan, run on card paint,
 *  not per frame. */
export function cachedEntitiesOfType(type: string): EntityDto[] {
  const out: EntityDto[] = [];
  for (const e of ENTITY_CACHE.values()) if (e.type === type) out.push(e);
  return out;
}

// Playback (timeline.ts): a non-null playhead re-bakes the live dots AS OF
// that instant — only events that had already happened, coloured and faded by
// their age AT THE PLAYHEAD, pulsing as they "occur". The events themselves
// keep arriving through setLiveEvents (the 30 s poll), so a replay opened
// mid-wave stays complete; the playhead only changes how they are baked.
let playbackAt: number | null = null;
let lastLiveEvents: EntityDto[] = [];
// Splash window in MAP time — the transport passes speed × a constant real
// duration, so every ring lives the same ~2.5 real seconds at any speed.
let playbackSplashMs = 30 * 60_000;
// Replay shows ONLY the window: nothing that happened before `from` draws at
// all, and an event stays for `lingerMs` of map time after it occurs. Both
// come from the transport; live rendering never reads them.
let playbackFrom = 0;
let playbackLinger = Infinity;
// The two replay grammars. 'live' is the live map run fast: the marker
// style's own announcement, the age ramp over liveTiming's colour hours, the
// fade over its fade hours, one marker per situation — the transport sets
// lingerMs to the fade window, so a dot leaves exactly when the live map
// would have dropped it. 'linger' is the fixed-stay mode: the red→grey arc is
// re-derived from the stay period so it completes before the dot leaves, and
// events stay individual so a burst reads as a burst.
let playbackGrammar: PlaybackGrammar = 'live';
// A real-time floor under every announcement (ring, halo, pulse, the
// collision-exempt glyph). A 10-minute announcement is 10 real seconds at ×60
// and 170 ms at ×3600 — one tick, invisible — so the transport passes
// speed × a constant real duration, exactly as it does for the splash ring,
// and an arrival is seen at any speed.
let playbackAnnounceFloor = 0;

export type PlaybackGrammar = 'live' | 'linger';

export interface PlaybackOpts {
  splashMs?: number;
  from?: number;
  lingerMs?: number;
  grammar?: PlaybackGrammar;
  announceFloorMs?: number;
}

/** Is an event at `t` on the replay map with the playhead at `at`? Only the
 *  window, only once it has happened, only while it lingers. */
export function inReplayWindow(t: number, at: number, from: number,
                               lingerMs: number): boolean {
  return t <= at && t >= from && at - t <= lingerMs;
}

/** An announcement lifted to the real-time floor. A style with no ring or
 *  pulse (0) never gains one from the floor — the floor stretches what the
 *  operator turned on, it does not turn things on. */
export function announceMs(ms: number, floorMs: number): number {
  return ms > 0 ? Math.max(ms, floorMs) : 0;
}

export function setPlaybackTime(map: maplibregl.Map, at: number | null,
                                opts?: PlaybackOpts): void {
  playbackAt = at;
  if (opts?.splashMs) playbackSplashMs = opts.splashMs;
  playbackFrom = opts?.from ?? 0;
  playbackLinger = opts?.lingerMs ?? Infinity;
  playbackGrammar = opts?.grammar ?? 'live';
  playbackAnnounceFloor = opts?.announceFloorMs ?? 0;
  bakeLiveDots(map);
}

// --- event colour rules ------------------------------------------------------
// Operator-set ring colours, applied in BOTH live and replay: a faction can be
// given a colour (Ansar Allah red, CENTCOM blue), and attack kinds
// (launch/airstrike/drone) vs impact kinds (impact/explosion) can each take a
// class colour. Faction beats class; nothing set means the defaults. The rules
// colour the RING — the replay splash, the live halo, the dot stroke — never
// the dot fill, which stays the age ramp: WHO is a ring question, HOW OLD is
// the fill's. Owned here (not timeline.ts) and loaded at module init so the
// live map is coloured without the transport ever opening.
/** Which question the ring answers. Explicit, not inferred from which colours
 *  happen to be set: with both configured, "faction beats class" is a rule the
 *  reader cannot see, and a map whose colouring you cannot NAME is a map you
 *  cannot trust. `age` leaves the ring alone entirely (the default).
 *
 *  LIVE and REPLAY carry separate modes — a per-surface choice, not one
 *  global: the live map watching for what-just-happened and a replay tracing
 *  who-hit-whom are different questions, and the operator asked to answer
 *  them differently. The palettes are shared; only the mode splits. */
export type ColorMode = 'age' | 'faction' | 'class';

export interface EventColorRules {
  liveMode: ColorMode;
  replayMode: ColorMode;
  factions: Record<string, string>;   // lowercased attributedTo -> css colour
  attacks: string | null;
  effects: string | null;
}

const COLOR_KEY = 'cockpit-event-colors';
const ATTACK_KINDS = new Set(['launch', 'airstrike', 'drone', 'artillery', 'gunfire',
                              'advance']);
const EFFECT_KINDS = new Set(['impact', 'explosion', 'fire']);

const _MODE = (v: unknown): ColorMode | null =>
  v === 'age' || v === 'faction' || v === 'class' ? v : null;

function loadColorRules(): EventColorRules {
  const base: EventColorRules =
    { liveMode: 'age', replayMode: 'age', factions: {}, attacks: null, effects: null };
  try {
    const saved = JSON.parse(localStorage.getItem(COLOR_KEY) || '{}');
    if (typeof saved.attacks === 'string') base.attacks = saved.attacks;
    if (typeof saved.effects === 'string') base.effects = saved.effects;
    if (saved.factions && typeof saved.factions === 'object') {
      for (const [k, v] of Object.entries(saved.factions)) {
        if (typeof v === 'string') base.factions[k.toLowerCase()] = v;
      }
    }
    // Two migrations: a blob with the short-lived single `mode` seeds both
    // surfaces; one older still (colours, no mode at all) infers from which
    // colours were set — an existing setup keeps its map either way.
    const legacy = _MODE(saved.mode)
      ?? (Object.keys(base.factions).length ? 'faction'
        : (base.attacks || base.effects) ? 'class' : 'age');
    base.liveMode = _MODE(saved.liveMode) ?? legacy;
    base.replayMode = _MODE(saved.replayMode) ?? legacy;
  } catch { /* a corrupt blob means defaults */ }
  return base;
}

let colorRules: EventColorRules = loadColorRules();

export function eventColorRules(): EventColorRules {
  return colorRules;
}

export function setEventColorRules(map: maplibregl.Map, rules: EventColorRules): void {
  colorRules = rules;
  localStorage.setItem(COLOR_KEY, JSON.stringify(rules));
  bakeLiveDots(map);
}

function ringColorOf(e: EntityDto, replay: boolean): string | null {
  const mode = replay ? colorRules.replayMode : colorRules.liveMode;
  if (mode === 'faction') {
    const fac = String(e.attrs.attributedTo ?? '').trim().toLowerCase();
    return (fac && colorRules.factions[fac]) || null;
  }
  if (mode === 'class') {
    const k = knownKind(e.attrs.kind);
    if (colorRules.attacks && ATTACK_KINDS.has(k)) return colorRules.attacks;
    if (colorRules.effects && EFFECT_KINDS.has(k)) return colorRules.effects;
  }
  return null;   // 'age' — the ring stays the age ramp
}

/** Distinct attributed factions across the loaded events, most-reported
 *  first — the colour picker's row list. */
export function liveEventFactions(): string[] {
  const counts = new Map<string, { n: number; name: string }>();
  for (const e of lastLiveEvents) {
    const raw = String(e.attrs.attributedTo ?? '').trim();
    if (!raw) continue;
    const key = raw.toLowerCase();
    const cur = counts.get(key) ?? { n: 0, name: raw };
    cur.n += 1;
    counts.set(key, cur);
  }
  return [...counts.values()].sort((a, b) => b.n - a.n)
    .slice(0, 12).map((v) => v.name);
}

// The linger ramp: the same red→orange→yellow→grey grammar as the live map,
// but spanning the LINGER period rather than the feed's configured hours —
// "one pulse then a grey dot" has to complete its arc before the dot leaves.
function lingerColor(p: number): string {
  return p < 0.25 ? AGE_COLORS[0]![1]
    : p < 0.55 ? AGE_COLORS[1]![1]
    : p < 0.8 ? AGE_COLORS[2]![1]
    : '#b0bec5';
}

function lingerOpacity(p: number): number {
  return p <= 0.6 ? 1 : Math.max(0.08, 1 - ((p - 0.6) / 0.4) * 0.92);
}

// --- the replay view --------------------------------------------------------
// Replay is its OWN view, not the operator's current one with a clock bolted
// on: the "now" picture (unit chips, status rings, labels, coverage) hides,
// areas and lines drop to faint context, damage shows in full (the ?asof tile
// clock decides what exists), and live events show regardless of the Events
// toggle. The feed's window/dismissals never applied to a replay (bake below).
// applyObjectFilter keeps recording what the panels WANT, so leaving replay
// restores exactly the state the Layers panel last asked for — toggles
// flipped mid-replay included.
let playbackView = false;
let lastFilterState: ObjectFilterState | null = null;
let replayVis: Record<string, 'visible' | 'none'> = {};
let replayPaint: [string, string, unknown][] = [];

// The replay view's OWN layer choices (the transport's Layers popover) —
// independent of the Layers panel, persisted by timeline.ts. `nowPicture` is
// the live-state clutter (unit chips, status rings, labels, coverage): off by
// default, but an operator replaying a strike against the mapped order of
// battle can turn it back on.
export interface ReplayLayers {
  events: boolean;
  damage: boolean;
  context: boolean;
  nowPicture: boolean;
  /** Flight paths from the 7-day ADS-B record, sliced per playhead
   *  (aircraft.ts's replay section). Its own toggle, independent of the
   *  Layers panel's live overlay — the layers themselves belong to
   *  aircraft.ts, so this flag is carried, never applied, here. */
  aircraft: boolean;
  /** Territorial control AS OF THE PLAYHEAD — the ?asof tile clock serves
   *  the territory versions valid then, so a replay across a capture shows
   *  the district change hands. On by default: it is the one thing a
   *  control layer can show that the live map cannot. */
  control: boolean;
  /** Per-kind event filter: a kind mapped to false is hidden from the replay
   *  bake (dots AND splash rings — the filter sits on the data). A missing
   *  key means on, so a kind added later never starts hidden. */
  kinds: Record<string, boolean>;
}

// Replay defaults to EVENTS ONLY — the mode exists to watch things happen,
// and everything else is context the operator opts into.
let replayLayers: ReplayLayers =
  { events: true, damage: false, context: false, nowPicture: false,
    aircraft: false, control: true, kinds: {} };

const REPLAY_NOW_LAYERS = ['coverage-fill', 'coverage-engage', 'coverage-radar',
                           'entities-ring', 'entities-symbol', 'entities-label'];
const REPLAY_CONTEXT_LAYERS = ['entities-fill', 'entities-outline', 'entities-line'];
// Territorial control has its OWN replay toggle and draws at full strength:
// under the ?asof tile clock the replay shows who held the ground AT THE
// PLAYHEAD, which is the one thing a control layer can show that a live map
// cannot, and fading it to context would hide the very change being replayed.
const REPLAY_CONTROL_LAYERS = CONTROL_LAYER_IDS;
// What a context layer's filter returns to in replay: no category or story
// narrowing, but never LESS than its standing filter — a null filter on the
// control fill would paint every polygon in the holder colour.
const REPLAY_BASE_FILTER: Record<string, unknown> = {
  'entities-fill': NOT_CONTROL, 'entities-outline': NOT_CONTROL,
  ...CONTROL_BASE_FILTERS,
};
const REPLAY_LIVE_LAYERS = LIVE_LAYER_IDS;
const REPLAY_TOUCHED = [...REPLAY_NOW_LAYERS, ...REPLAY_CONTEXT_LAYERS,
                        ...REPLAY_CONTROL_LAYERS, 'entities-damage',
                        ...REPLAY_LIVE_LAYERS];
const REPLAY_FAINT: [string, string, unknown][] = [
  ['entities-fill', 'fill-opacity', 0.04],
  ['entities-outline', 'line-opacity', 0.3],
  ['entities-line', 'line-opacity', 0.3],
];

export function setReplayLayers(map: maplibregl.Map, rl: ReplayLayers): void {
  replayLayers = rl;
  if (playbackView) {
    applyReplayView(map);
    bakeLiveDots(map);   // the kind filter lives in the bake, not a layer
  }
}

export function setPlaybackView(map: maplibregl.Map, on: boolean): void {
  if (playbackView === on) return;
  playbackView = on;
  if (on) {
    // Snapshot ONCE at entry — applyReplayView re-runs on every layer toggle,
    // and re-snapshotting then would capture replay state as "before".
    replayVis = {};
    replayPaint = [];
    for (const id of REPLAY_TOUCHED) {
      if (!map.getLayer(id)) continue;
      replayVis[id] =
        (map.getLayoutProperty(id, 'visibility') as 'visible' | 'none' | undefined) ?? 'visible';
    }
    for (const [id, prop] of REPLAY_FAINT) {
      if (!map.getLayer(id)) continue;
      replayPaint.push([id, prop, map.getPaintProperty(id, prop as never)]);
    }
    applyReplayView(map);
    return;
  }
  for (const [id, vis] of Object.entries(replayVis)) {
    if (map.getLayer(id)) map.setLayoutProperty(id, 'visibility', vis);
  }
  for (const [id, prop, value] of replayPaint) {
    // A value never set at creation restores as undefined = the spec default,
    // which is exactly what "never set" was.
    if (map.getLayer(id)) map.setPaintProperty(id, prop as never, value as never);
  }
  replayVis = {};
  replayPaint = [];
  if (lastFilterState) applyObjectFilter(map, lastFilterState);
}

function setVis(map: maplibregl.Map, id: string, on: boolean): void {
  if (map.getLayer(id)) {
    map.setLayoutProperty(id, 'visibility', on ? 'visible' : 'none');
  }
}

function applyReplayView(map: maplibregl.Map): void {
  const rl = replayLayers;
  // The "now" picture keeps the panels' own filters when shown — replay only
  // decides WHETHER it draws, not what the operator's filters selected.
  for (const id of REPLAY_NOW_LAYERS) setVis(map, id, rl.nowPicture);
  // Context geometry: every area/line, faint, no category or story narrowing.
  for (const id of REPLAY_CONTEXT_LAYERS) {
    if (!map.getLayer(id)) continue;
    setVis(map, id, rl.context);
    map.setFilter(id, (REPLAY_BASE_FILTER[id] ?? null) as never);
  }
  // Control as of the playhead: no story narrowing, its standing filters kept.
  for (const id of REPLAY_CONTROL_LAYERS) {
    if (!map.getLayer(id)) continue;
    setVis(map, id, rl.control);
    map.setFilter(id, REPLAY_BASE_FILTER[id] as never);
  }
  for (const [id, prop, value] of REPLAY_FAINT) {
    if (map.getLayer(id)) map.setPaintProperty(id, prop as never, value as never);
  }
  // Damage in full: no date window, no story scope, no zoom gate — what
  // existed at the playhead is the asof tile clock's question, not a filter's.
  if (map.getLayer('entities-damage')) {
    setVis(map, 'entities-damage', rl.damage);
    map.setFilter('entities-damage', ['==', ['get', 'type_key'], 'damage_marker'] as never);
  }
  for (const id of REPLAY_LIVE_LAYERS) setVis(map, id, rl.events);
}

// Occurrence instants for the timeline's tick marks — the transport draws
// where the bursts are so a reader can aim the playhead before pressing play.
export function liveEventTimes(): number[] {
  return lastLiveEvents.map((e) => whenOf(e).at);
}

export function setLiveEvents(map: maplibregl.Map, events: EntityDto[]): void {
  cacheEntities(events);
  lastLiveEvents = events;
  // Alerts ride the same hook as the dots — one call site, so the area flash
  // can never drift from what the map is showing. Deliberately on the REAL
  // clock even mid-playback: sirens sounding now must interrupt a replay.
  updateAlerts(map, events);
  bakeLiveDots(map);
}

// The marker style is per-KIND and changes what a marker looks like over time,
// so the bake has to be re-run when nothing at all has arrived — a ring stops,
// a pulse stops, a colour stage begins. Rather than poll (a 15 s timer makes a
// 5 s stage invisible; a 1 s timer re-serialises the whole map once a second
// forever), each bake computes when the SOONEST of those boundaries falls and
// schedules exactly one re-bake there.
let rebakeTimer = 0;
/** Where a fanned-out marker is DRAWN, by entity id — only markers the spread
 *  moved. The selection halo reads it so the ring lands on the dot the
 *  operator clicked, not on the shared point three markers are fanned around. */
const spreadPos = new Map<string, [number, number]>();

function scheduleRebake(map: maplibregl.Map, deltaMs: number): void {
  if (rebakeTimer) {
    clearTimeout(rebakeTimer);
    rebakeTimer = 0;
  }
  if (!Number.isFinite(deltaMs)) return;
  rebakeTimer = window.setTimeout(() => {
    rebakeTimer = 0;
    bakeLiveDots(map);
  }, Math.max(250, Math.min(deltaMs + 50, 300_000)));
}

function bakeLiveDots(map: maplibregl.Map): void {
  const source = map.getSource(LIVE) as maplibregl.GeoJSONSource | undefined;
  if (!source) return;
  const at = playbackAt;
  const now = at ?? Date.now();
  const freshMs = liveFreshMs();
  const events = at === null
    ? lastLiveEvents.filter((e) => visibilityOf(e) !== 'hidden')
    // History: ONLY the window, and only while the event lingers. Nothing
    // from before the window start draws at all — a replay opened at the
    // window's first second starts on an empty map — and once the linger
    // period passes, the dot leaves instead of piling up grey. visibilityOf
    // is a Date.now() question and does not apply to a replay.
    : lastLiveEvents.filter((e) =>
        inReplayWindow(whenOf(e).at, at, playbackFrom, playbackLinger)
          && replayLayers.kinds[knownKind(e.attrs.kind)] !== false);
  // ONE MARKER PER SITUATION on the live map. Several reports of one strike
  // each carry their own estimated position, so the map drew N pins for one
  // thing — a cluster of near-duplicates that reads as N events.
  //
  // groupFeed, NOT groupSituations, and the difference is the whole point:
  // groupFeed attaches routed members BY INCIDENT ID first and only falls
  // back to the client heuristics for what the router has not placed. The
  // heuristics link on place name and proximity, so four reports of one
  // UKMTO tanker strike filed as "Lima", "Lima", "Strait of Hormuz" and
  // "Oman" bridge only the two that share a name — while the server's
  // routing, which reads the texts, folds all four. Grouping the map by the
  // weaker of the two would silently discard that work, and would put the
  // map and the feed into disagreement about what counts as one event.
  //
  // A representative event carries the appearance (kind glyph, colour rules,
  // sprites) so the whole per-event pipeline below runs untouched — the
  // headline member, exactly the one the card titles itself with. Only three
  // things come from the GROUP: the position (situationAnchor's busiest
  // ~2 km cell, not a naive centroid that would land between two towns), the
  // freshness clocks (a situation is as recent as its newest member — a card
  // that grew re-enters "new"), and the count.
  //
  // A replay in the LIVE grammar groups exactly as the live map does — it is
  // the live map run fast, and a picture that drew five pins for one strike
  // would not be. A fixed stay keeps events individual: that mode is about
  // moments landing, and how many landed is the point. The splash rings are
  // per event under both, so a burst reads as a burst either way.
  const grouped = at === null || playbackGrammar === 'live';
  const items: { e: EntityDto; t: number; last: number;
                 geom: EntityDto['geometry']; count: number }[] =
    grouped
      ? groupFeed(events.filter((e) => e.geometry?.type === 'Point'))
          .map((sit) => {
            const head = headlineOf(sit.events);
            const anchor = situationAnchor(sit.events);
            return {
              e: head,
              t: Math.max(...sit.events.map((m) => whenOf(m).at)),
              last: Math.max(...sit.events.map((m) => lastReportOf(m))),
              geom: anchor
                ? { type: 'Point' as const, coordinates: anchor }
                : head.geometry,
              count: sit.events.length,
            };
          })
      : events.filter((e) => e.geometry?.type === 'Point')
          .map((e) => ({ e, t: whenOf(e).at, last: lastReportOf(e),
                         geom: e.geometry, count: 1 }));

  // STACKED MARKERS FAN OUT. Grouping already folds one situation's reports
  // into one marker; this is the other case — different situations that the
  // gazetteer put on the identical point (every "Tehran" is the same
  // coordinate) or close enough to overlap at this zoom. Each gets pushed onto
  // a ring around the shared point with a leg back to it, laid out oldest
  // first so a new arrival takes the next slot rather than reshuffling the
  // fan. The dot's true position is kept for the selection halo.
  const fanned = spread(
    items.map(({ geom, t }) => {
      const [lon, lat] = (geom as Point).coordinates as [number, number];
      return { lon, lat, order: t };
    }),
    map.getZoom(),
  );
  const legs: Feature[] = [];
  spreadPos.clear();

  let soonest = Infinity;
  source.setData({
    type: 'FeatureCollection',
    // COLOUR BY OCCURRENCE, PULSE BY LAST REPORT — two different questions.
    // Colour asks "how old is this", so it reads the occurrence clock; the
    // pulse asks "is this still developing", so it reads the report clock. A
    // strike from six hours ago that a channel just corroborated then draws as
    // a grey dot WITH a pulse, which is exactly what it is. Both used to read
    // valid_from, so a merge repainted an old event as brand new.
    features: items
      .map(({ e, t, last, count }, i) => {
        const fan = fanned[i]!;
        const geom: Point = { type: 'Point', coordinates: [fan.lon, fan.lat] };
        if (fan.from) spreadPos.set(e.id, [fan.lon, fan.lat]);
        const kind = knownKind(e.attrs.kind);
        const style = styleFor(kind);
        // A fixed-stay replay ages along the LINGER arc, not the live feed's
        // configured hours — the dot must complete red→grey→gone within its
        // stay. The live grammar (and a stay-to-end replay) keeps the live
        // ramps, asked "how old was this at the playhead".
        const p = at !== null && playbackGrammar === 'linger'
          && Number.isFinite(playbackLinger) ? (at - t) / playbackLinger : null;
        // Age on the REPORT clock drives every announcement; age on the
        // OCCURRENCE clock drives colour. In a replay there is no report clock
        // at all, so appearance-since-the-playhead stands in for it — and each
        // announcement is lifted to the real-time floor, so it is seen at any
        // speed. Live is untouched: the floor is 0 there.
        const reportAge = at === null ? now - last : at - t;
        const occurrenceAge = now - t;
        const lift = (ms: number): number =>
          at === null ? ms : announceMs(ms, playbackAnnounceFloor);
        const flash = style.ring && reportAge < lift(ringMs(style, freshMs));
        const pulsing = reportAge < lift(pulseMs(style, freshMs));
        const isNew = reportAge < lift(newMs(style, freshMs));

        const stage = p !== null ? null : stageAt(style, occurrenceAge);
        const fillColor = stage ? stage.color
          : p !== null ? lingerColor(p) : ageColor(t, now);
        const opacity = (stage ? stage.opacity : 1)
          * (p !== null ? lingerOpacity(p) : ageOpacity(t, now));
        // Operator colour rules ride as the RING colour (halo + dot stroke);
        // null falls through the dot layer's coalesce to the default stroke.
        const rule = ringColorOf(e, at !== null);
        // `settleScale` takes over the moment the marker stops being new. The
        // BEAT itself only reaches the glyph, which is a sprite and can redraw
        // itself; the dot is a circle layer, so it takes the beat's midpoint as
        // a standing size bump instead — visibly bigger while new, at no
        // per-frame cost. Applying `bodyScale` to the glyph as well would
        // double-count it against the sprite's own animation.
        const bodyScale = isNew ? 1 : style.settleScale;
        const dotR = style.dotPx * bodyScale * (pulsing ? 1 + style.pulsePct / 200 : 1);

        // A ring that follows the marker colour prefers the operator's own
        // faction/class rule over the age ramp — same precedence as the halo,
        // so "who" beats "how old" wherever the operator has said who.
        const ringImg = flash
          ? ringSprite(map, style, style.ringColor || rule || fillColor) : '';

        if (at === null) {
          soonest = Math.min(soonest,
            nextBoundaryMs(style, reportAge, occurrenceAge, freshMs));
        }
        if (fan.from) {
          legs.push({
            type: 'Feature',
            geometry: { type: 'LineString', coordinates: [fan.from, [fan.lon, fan.lat]] },
            properties: { color: rule || fillColor, opacity },
          });
        }
        return {
          type: 'Feature' as const,
          geometry: geom,
          properties: {
            id: e.id,
            // How many reports this one marker stands for. 1 renders no badge.
            count,
            kind: String(e.attrs.kind ?? ''),
            glyph: kind,                    // sprite-safe: unknown -> unclear
            fillColor,
            ring: rule,
            opacity,
            isNew,
            // --- the arrival ring ---
            // Gated on the sprite existing: past the sprite ceiling ringSprite
            // returns '', and a symbol layer pointed at a missing image logs a
            // warning every frame. No ring is the honest degradation.
            flash: ringImg !== '',
            ringImg,
            // --- halo ---
            halo: style.halo && isNew,
            haloColor: style.haloColor || rule || fillColor,
            haloR: style.haloPx,
            haloOpacity: style.haloOpacity,
            // --- body ---
            dot: style.dot,
            dotR,
            symbol: style.symbol,
            symSize: style.symbolScale * bodyScale,
            // pulseMs() is already 0 when the pulse is off or its intensity is
            // zero, so `pulsing` alone decides which sprite this marker uses.
            glyphImg: glyphSprite(map, style, kind, pulsing,
                                  style.symbolTint ? fillColor : ''),
          },
        };
      }),
  });
  (map.getSource(LEGS) as maplibregl.GeoJSONSource | undefined)
    ?.setData({ type: 'FeatureCollection', features: legs });
  if (at === null) scheduleRebake(map, soonest);
  else scheduleRebake(map, Infinity);

  // Replay splash rings: an event within its splash window behind the
  // playhead gets a ring at progress p = 0..1 — the 120 ms re-bake IS the
  // animation. Scrubbing shows the same rings frozen mid-expansion; live
  // (at === null) clears them.
  const splash = map.getSource(SPLASH) as maplibregl.GeoJSONSource | undefined;
  if (!splash) return;
  splash.setData({
    type: 'FeatureCollection',
    features: at === null ? [] : events
      .filter((e) => e.geometry?.type === 'Point')
      .filter((e) => at - whenOf(e).at < playbackSplashMs)
      .map((e) => ({
        type: 'Feature' as const,
        geometry: e.geometry!,
        properties: {
          p: (at - whenOf(e).at) / playbackSplashMs,
          color: ringColorOf(e, true),
        },
      })),
  });
}

/** Does this changed entity require the vector tiles to be re-fetched?
 *
 *  NOT every geometry change does, and the difference is most of this client's
 *  network traffic. A LIVE EVENT reaches the map through the client-side
 *  `live-events` GeoJSON source (setLiveEvents), and no tile layer draws one:
 *  entities-symbol / -ring / -label all filter out NON_UNIT_TYPES, and
 *  entities-damage targets damage_marker alone. So a live event needs no tile
 *  work whatsoever — while bumping for it calls setTiles, which tears down
 *  every visible tile, ABORTS whatever is in flight (the NS_BINDING_ABORTED
 *  lines filling the console) and busts Martin's 10-minute cache server-side.
 *
 *  Live events are also the highest-frequency update by a wide margin, so
 *  this is the difference between re-fetching the whole viewport on almost
 *  every poll and re-fetching it when something the tiles actually draw has
 *  changed. Damage markers, places, areas and units all still bump.
 *
 *  If a tile layer is ever added that DOES draw live events, this is the
 *  function that has to change with it. */
export function needsTileRefresh(e: { geometry?: unknown; type?: string }): boolean {
  return !!e.geometry && e.type !== 'live_event';
}

export function bumpTiles(map: maplibregl.Map, rev: number): void {
  if (rev <= tileVersion) return;
  tileVersion = rev;
  tileBumps += 1;
  const source = map.getSource(SRC) as maplibregl.VectorTileSource | undefined;
  source?.setTiles([tileUrl()]);
}

/** Counters behind window.sentinel.stats() — "are the tiles churning?" was
 *  only answerable by reading the network tab and guessing. */
let tileBumps = 0;
export function tileStats(): { version: number; bust: number; bumps: number } {
  return { version: tileVersion, bust: tileBust, bumps: tileBumps };
}

// After a delete (204, no rev) the tiles must refresh NOW, not on the next
// updates poll — a ghost marker lingering 15 s reads as a bug.
export function forceTileRefresh(map: maplibregl.Map): void {
  tileBust += 1;
  const source = map.getSource(SRC) as maplibregl.VectorTileSource | undefined;
  source?.setTiles([tileUrl()]);
}

export function setSelected(map: maplibregl.Map, entities: EntityDto[],
                            extraFeatures: Feature[] = []): void {
  const source = map.getSource(SEL) as maplibregl.GeoJSONSource | undefined;
  source?.setData({
    type: 'FeatureCollection',
    features: [
      ...entities
        .filter((e) => e.geometry)
        .map((e) => {
          // A live marker the spread fanned out is highlighted where it is
          // DRAWN; the stored geometry is where the leg points back to.
          const drawn = spreadPos.get(e.id);
          const geometry: EntityDto['geometry'] = drawn
            ? { type: 'Point', coordinates: drawn } : e.geometry!;
          return { type: 'Feature' as const, geometry,
                   properties: { id: e.id, type_key: e.type } };
        }),
      ...extraFeatures,
    ],
  });
}

/** Click priority, strongest claim first: precise point targets, then the
 *  unit ring (26 px of mostly empty halo — a generous unit hit target, but it
 *  must not swallow a marker sitting inside it), then lines, then area fills
 *  last — a polygon is the easiest thing to hit again after a miss. */
export const CLICK_PRIORITY: readonly string[] = [
  ...LIVE_CLICK_IDS, 'entities-symbol', 'entities-damage',
  'entities-ring', 'entities-line', 'entities-fill',
  // Ground last: a territory is country-sized, so it only wins a click that
  // hits nothing else — and it gets no pointer cursor, since half the map
  // reading as a link would be noise.
  'control-fill',
];

/** The hit that wins a click. Ties (same layer) keep query order, which is
 *  topmost-rendered first. Exported for the test. */
export function bestClickHit<T extends { layer: { id: string } }>(
  hits: readonly T[],
): T | undefined {
  let best: T | undefined;
  let bestRank = Infinity;
  for (const h of hits) {
    const rank = CLICK_PRIORITY.indexOf(h.layer.id);
    if (rank !== -1 && rank < bestRank) { best = h; bestRank = rank; }
  }
  return best;
}

export function installObjectInteractions(
  map: maplibregl.Map,
  onPick: (id: string, lngLat: maplibregl.LngLat) => void,
): void {
  // ONE handler over every clickable layer, not one per layer. Per-layer
  // handlers all fire for a stacked click — a damage marker inside a watched
  // area fired the marker's AND the fill's, each calling onPick, and the
  // last-registered selection won, which made every point inside a polygon
  // unselectable (and during a map-pick, consumed the pick with one feature
  // then navigated to the other).
  map.on('click', (e) => {
    const hits = map.queryRenderedFeatures(e.point, { layers: [...CLICK_PRIORITY] })
      .filter((f) => f.properties?.id);
    const hit = bestClickHit(hits);
    const id = hit?.properties?.id as string | undefined;
    if (id) onPick(id, e.lngLat);
  });
  // ONE hover registration over every clickable layer (hover.ts): a
  // mouseenter/mouseleave pair per layer cost two feature queries per layer
  // per mouse move. Ground gets no pointer cursor — half the map reading as a
  // link would be noise.
  registerHover(map, {
    layers: CLICK_PRIORITY.filter((layer) => layer !== 'control-fill'),
    cursor: 'pointer',
  });
}
